import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './Onboarding.css';
import EditableServiceCard from '../components/EditableServiceCard';
import BusinessInfoForm from '../components/BusinessInfoForm';
import BusinessScheduleForm from '../components/BusinessScheduleForm';
import SelectorDeColores from '../components/business/SelectorDeColores';
import SelectorDeImagen from '../components/business/SelectorDeImagen';
import { Landmark, Wallet, Zap } from 'lucide-react';
import VistaPreviaNegocio from '../components/VistaPreviaNegocio';
import PasoNavegador from '../components/PasoNavegador';
import TermsOfService from '../components/TermsOfService';
import PrivacyPolicy from '../components/PrivacyPolicy';
import mercadoPagoLogo from '../assets/images/Mercado/mercadopago.webp';
import { ULTIMA_ACTUALIZACION } from '../data/legal';
import { PricingSection } from '../components/pricing-section';
// Los rubros viven en un módulo aparte para que la landing use exactamente los
// mismos que el onboarding y no se desincronicen.
import { businessTemplates } from '../data/rubros';
// El naranja de Aturno vive en un solo lugar; acá se usa como color inicial de
// la marca del negocio, para que no arranque con un color que no es de la app.
import { COLORES_POR_DEFECTO } from '../lib/branding';
import {
  catalogoDeRoles,
  cuantosUsanElRol,
  normalizarRol,
  quitarRolDelEquipo,
  rolesDe,
  rolParaGuardar,
} from '../lib/roles';
import FormularioDePersona from '../components/business/FormularioDePersona';
import { nombreDePlan } from '../data/planes';
import { toast } from 'sonner';
import { API_URL } from '../config/api';

// API URL

// Configuración de Mercado Pago OAuth
const MERCADO_PAGO_CONFIG = {
    CLIENT_ID: '8962166513954821', // Cliente ID de MercadoPago
    REDIRECT_URI: 'https://aturno-backend.onrender.com/api/oauth/mercadopago/callback', // Callback del backend
    AUTH_URL: 'https://auth.mercadopago.com.ar/authorization'
};

// Constantes
const dayNames = {
    monday: 'Lunes',
    tuesday: 'Martes', 
    wednesday: 'Miércoles',
    thursday: 'Jueves',
    friday: 'Viernes',
    saturday: 'Sábado',
    sunday: 'Domingo'
};

const hours = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'));
const minutes = ['00', '15', '30', '45'];


/**
 * `pasoInicial` y `datosIniciales` existen solo para la ruta de pruebas de
 * desarrollo (pages/DevOnboarding.jsx), que necesita abrir un paso puntual con
 * datos ya cargados para medirlo. En la app real Onboarding se monta sin props
 * y arranca vacío en el paso 1, como siempre.
 */
export default function Onboarding({ pasoInicial, datosIniciales }) {
    const navigate = useNavigate();
    const { currentUser, refreshUserData, userData } = useAuth();
    
    // Verificar si el usuario ya completó el onboarding
    useEffect(() => {
        if (userData && userData.onboardingCompleted) {
            navigate('/admin/dashboard', { replace: true });
        }
    }, [userData, navigate, currentUser]);
    
    // Arranca en 1: el paso 0 elegía entre cuenta individual y de negocio, y sin
    // planes personales no hay nada que elegir.
    const [step, setStep] = useState(pasoInicial || 1);
    /**
     * Los pasos que se muestran, en orden.
     *
     * El 6 era el paso de pagos y se eliminó, así que la numeración interna
     * tiene un agujero: no se puede contar con `step` como si fuera la posición.
     * Antes había un `totalSteps = 10` fijo mientras el último paso es el 11, y
     * el contador terminaba diciendo "Paso 11 de 10" (y "Paso 0 de 10" al
     * arrancar).
     *
     * Un usuario individual además se saltea personal (4) y recursos (5), así
     * que el total depende del tipo de cuenta.
     */
    const PASOS = [1, 2, 3, 4, 5, 7, 8, 9, 10, 11];
    const [planCategory, setPlanCategory] = useState('personal'); // 'personal' o 'business'
    const [billingCycle, setBillingCycle] = useState('annual'); // 'monthly' o 'annual' - anual por defecto

    const [formData, setFormData] = useState({
        // Ya no se pregunta: el plan personal se discontinuó, así que todas las
        // cuentas son de negocio. Se mantiene el campo porque el backend y el
        // resumen lo leen.
        userType: 'business',
        businessType: '',
        businessInfo: {
            name: '',
            email: '',
            slug: '',
            address: '',
            phone: '',
            // Mensaje libre que el negocio muestra en su página pública. Si queda
            // vacío, la página no renderiza nada en su lugar.
            mensaje: ''
        },
        schedule: {
            monday: { enabled: true, ranges: [{ start: '09:00', end: '18:00' }] },
            tuesday: { enabled: true, ranges: [{ start: '09:00', end: '18:00' }] },
            wednesday: { enabled: true, ranges: [{ start: '09:00', end: '18:00' }] },
            thursday: { enabled: true, ranges: [{ start: '09:00', end: '18:00' }] },
            friday: { enabled: true, ranges: [{ start: '09:00', end: '18:00' }] },
            saturday: { enabled: false, ranges: [{ start: '09:00', end: '13:00' }] },
            sunday: { enabled: false, ranges: [{ start: '09:00', end: '13:00' }] },
        },
        services: [],
        staff: [],
        resources: [],
        branding: {
            primaryColor: COLORES_POR_DEFECTO.primaryColor,
            logo: null
        },
        payments: {
            methods: [],
            deposit: 0
        },
        integrations: {
            googleCalendar: false,
            mercadoPago: false
        },
        compliance: {
            privacy: false,
            terms: false,
            consent: false
        },
        selectedPlan: '', // Nuevo campo para el plan seleccionado
        selectedBillingCycle: 'annual', // 'monthly' o 'annual'
        ...datosIniciales
    });

    /**
     * La secuencia real de pasos y la posición del actual dentro de ella. El
     * contador y la barra se calculan de acá, no de `step`, que tiene el agujero
     * del paso 6 eliminado.
     *
     * El paso 5 (espacios reservables) aparece solo en los rubros donde el lugar
     * es lo que se reserva: una cancha, una sala, un aula. En una peluquería o
     * un consultorio lo que limita cuántos turnos entran a la misma hora es la
     * persona, que se carga en el paso anterior, y un espacio solo hace algo si
     * después se lo asigna a un servicio: era un paso obligatorio que no
     * cambiaba nada. Esos rubros hacen un onboarding de nueve pasos.
     *
     * Se busca en businessTemplates y no con getCurrentTemplate() porque esa
     * función se declara más abajo, y acá todavía estaría en su zona muerta.
     */
    const usaEspaciosReservables =
        businessTemplates.find(t => t.id === formData.businessType)?.template
            ?.usaEspaciosReservables === true;
    const pasosBase = usaEspaciosReservables ? PASOS : PASOS.filter(p => p !== 5);

    /**
     * Si el paso actual no está en la lista, se lo agrega igual.
     *
     * Puede pasar al cambiar de rubro estando parado en el paso de espacios: la
     * lista deja de incluirlo, `posicionDelPaso` no lo encuentra y devuelve 1,
     * así que el navegador cree que estás en el primer paso y APAGA la flecha de
     * atrás. Es decir: quedás encerrado en un paso que ya no existe.
     *
     * Manteniéndolo en la lista, la navegación sigue funcionando y el propio
     * efecto de abajo te saca de ahí.
     */
    const pasosVisibles = pasosBase.includes(step)
        ? pasosBase
        : [...pasosBase, step].sort((a, b) => a - b);
    const posicionDelPaso = Math.max(1, pasosVisibles.indexOf(step) + 1);

    useEffect(() => {
        // Salir del paso de espacios si el rubro elegido dejó de tenerlo. Corre
        // después de pintar, así que el paso llega a verse un instante; lo que
        // importa es que no se pueda quedar ahí.
        if (!pasosBase.includes(step)) {
            const siguiente = pasosBase.find(p => p > step) || pasosBase[pasosBase.length - 1];
            setStep(siguiente);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step, usaEspaciosReservables]);

    // Lo que se le pasa a la página pública para mostrarla con los datos que
    // todavía no se guardaron. Es el contrato que espera BusinessPage.
    const datosParaPreview = {
        businessInfo: formData.businessInfo,
        // Suelta y explícita, como la manda el panel: BusinessPage la acepta en
        // `datosPreview.logo` además de adentro de `businessInfo`. Sin esto la
        // foto se sube bien pero la vista previa de abajo sigue mostrando las
        // iniciales, que es justo lo que uno está tratando de cambiar.
        logo: formData.branding?.logo || null,
        services: formData.services,
        staff: formData.staff,
        resources: formData.resources,
        schedule: formData.schedule,
        branding: formData.branding,
    };

    // "Otro" se separa del resto para mostrarlo aparte en el paso de rubro.
    const rubrosConcretos = businessTemplates.filter((t) => t.id !== 'other');
    const rubroOtro = businessTemplates.find((t) => t.id === 'other');

    // DEBUG: Monitor userType changes
    useEffect(() => {
        if (formData) {
            // User type changed
        }
    }, [formData?.userType]);
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [editingStaff, setEditingStaff] = useState(null);
    const [showNewStaffForm, setShowNewStaffForm] = useState(false);
    /**
     * Alta de personal.
     *
     * Es estado controlado y no lectura del DOM con getElementById, que era como
     * estaba: así el horario propio puede convivir con el resto del formulario
     * sin perderse al redibujar.
     *
     * `roles` es una LISTA y va aparte del nombre. Antes no existía el campo:
     * los roles del rubro se ofrecían como atajos que abrían el formulario con
     * el puesto escrito en el campo Nombre, así que elegir "Estilista" daba de
     * alta a una persona llamada Estilista. Después fue un texto suelto, uno por
     * persona, y ahora son etiquetas que se prenden — porque una persona puede
     * hacer dos cosas (cardiólogo y nutricionista) y porque escribir el mismo
     * puesto en cada alta es donde se cuelan los duplicados por una tilde.
     * `rol` se sigue escribiendo al guardar, ver lib/roles.js.
     */
    const staffVacio = { name: '', roles: [], foto: null, email: '', phone: '', horarioPropio: false, schedule: null };

    /**
     * Borra un rol de todo el equipo.
     *
     * El catálogo de etiquetas se calcula a partir de los roles que la gente
     * tiene puestos (lib/roles.js), así que sacárselo a todos ES borrar la
     * etiqueta: no hay ninguna lista aparte de la que eliminarlo.
     *
     * Se ofrece deshacer porque el borrado toca a varias personas de una y no
     * hay forma de verlas a todas desde acá: quien se equivoca de etiqueta no
     * tendría manera de saber a quiénes se la sacó. Guardar el array anterior
     * alcanza —es el mismo estado, sin copiar nada— y es más barato que un
     * cartel de confirmación que se aprende a aceptar sin leer.
     */
    const borrarRolDelEquipo = (rol) => {
        const anterior = formData.staff;
        const cuantos = cuantosUsanElRol(anterior, rol);

        setFormData(prev => ({ ...prev, staff: quitarRolDelEquipo(prev.staff, rol) }));
        setNuevoStaff(p => ({ ...p, roles: (p.roles || []).filter(r => normalizarRol(r) !== normalizarRol(rol)) }));
        setEditingStaff(prev =>
            prev ? { ...prev, roles: rolesDe(prev).filter(r => normalizarRol(r) !== normalizarRol(rol)) } : prev
        );

        toast.success(`Rol "${rol}" borrado`, {
            description: cuantos > 0
                ? `Se lo sacamos a ${cuantos} ${cuantos === 1 ? 'persona' : 'personas'}.`
                : 'No lo tenía nadie puesto.',
            action: {
                label: 'Deshacer',
                onClick: () => setFormData(prev => ({ ...prev, staff: anterior })),
            },
        });
    };
    const [nuevoStaff, setNuevoStaff] = useState(staffVacio);
    // Error del alta de personal. Antes esto salía por alert(), que bloquea la
    // página y no se puede estilar.
    const [errorStaff, setErrorStaff] = useState('');
    // Acá vivía newStaffSchedule, el horario que se cargaba a mano en el alta de
    // cada empleado. Quedó sin uso: ahora la persona hereda el horario del
    // negocio y lo ajusta después desde el panel.
    /**
     * Si el negocio tiene un solo espacio reservable o varios. Arranca en null:
     * hasta que no se responde, el paso muestra solo la pregunta.
     *
     * Se deduce de lo que ya haya cargado para que volver al paso no borre la
     * respuesta. Un solo recurso equivale a haber contestado "uno solo", que es
     * lo mismo que deja el botón.
     */
    const [modoRecursos, setModoRecursos] = useState(null);

    const [isTransitioning, setIsTransitioning] = useState(false);

    // Función auxiliar para obtener los horarios del negocio para un día específico
    const getBusinessHoursForDay = (day) => {
        console.log('🔍 Onboarding - getBusinessHoursForDay:', { day, formDataSchedule: formData.schedule });
        if (formData.schedule && formData.schedule[day] && formData.schedule[day].enabled && formData.schedule[day].ranges && formData.schedule[day].ranges.length > 0) {
            console.log('✅ Onboarding - Usando horarios del negocio:', formData.schedule[day].ranges);
            return formData.schedule[day].ranges;
        }
        console.log('⚠️ Onboarding - Usando horarios por defecto');
        return [{ start: '09:00', end: '17:00' }]; // fallback por defecto
    };

    // Función para validar que los días habilitados tengan horarios
    const validateSchedule = (schedule) => {
        const enabledDaysWithoutRanges = Object.entries(schedule).filter(([day, dayData]) => 
            dayData.enabled && (!dayData.ranges || dayData.ranges.length === 0)
        );
        
        if (enabledDaysWithoutRanges.length > 0) {
            const dayNames = {
                monday: 'Lunes', tuesday: 'Martes', wednesday: 'Miércoles', 
                thursday: 'Jueves', friday: 'Viernes', saturday: 'Sábado', sunday: 'Domingo'
            };
            const invalidDayNames = enabledDaysWithoutRanges.map(([day]) => dayNames[day]);
            return {
                isValid: false,
                message: `❌ Los siguientes días están habilitados pero no tienen horarios configurados: ${invalidDayNames.join(', ')}\n\n📋 Para solucionarlo:\n1. Desactiva esos días, o\n2. Agrega horarios haciendo clic en "+ Agregar rango"`
            };
        }
        
        return { isValid: true };
    };
    
    // Estados para modal de recursos
    const [showAddResourceModal, setShowAddResourceModal] = useState(false);
    const [newResourceData, setNewResourceData] = useState({
        name: '',
        description: '',
        active: true,
        schedule: {
            monday: { enabled: false, ranges: [] },
            tuesday: { enabled: false, ranges: [] },
            wednesday: { enabled: false, ranges: [] },
            thursday: { enabled: false, ranges: [] },
            friday: { enabled: false, ranges: [] },
            saturday: { enabled: false, ranges: [] },
            sunday: { enabled: false, ranges: [] }
        }
    });
    const [editingResourceIndex, setEditingResourceIndex] = useState(null);
    /* Estos dos modales ya existían y no los abría nadie: sólo tenían el botón
       de cerrar. Los conecta la casilla de aceptación del paso final. Se leen
       acá adentro y no en /legal/*: irse a otra página en el último paso del
       onboarding es arriesgarse a perder todo lo cargado. */
    const [showTermsModal, setShowTermsModal] = useState(false);
    const [showPrivacyModal, setShowPrivacyModal] = useState(false);
    const [aceptaLegales, setAceptaLegales] = useState(false);
    
    // Estados para integraciones (solo MVP)
    const [integrations, setIntegrations] = useState({
        googleCalendar: { active: false, connected: false, email: "" },
        mercadoPago: { active: false, connected: false, accessToken: "" }
    });
    const [connectingIntegration, setConnectingIntegration] = useState(null);

    // Estados para el modal de agregar/editar servicio
    const [showServiceModal, setShowServiceModal] = useState(false);
    const [editingServiceIndex, setEditingServiceIndex] = useState(null);
    const [serviceModalData, setServiceModalData] = useState(null);

    // Get current template
    const getCurrentTemplate = () => {
        if (!formData || !formData.businessType) {
            return businessTemplates.find(t => t.id === 'other').template;
        }
        const template = businessTemplates.find(t => t.id === formData.businessType);
        return template?.template || businessTemplates.find(t => t.id === 'other').template;
    };

    // Recursos sugeridos del rubro, normalizados a { name, description }.
    // Tolera el formato viejo (array de strings) por si algún template lo usa.
    const getRecursosSugeridos = () => {
        const sugeridos = getCurrentTemplate().suggestedResources || [];
        return sugeridos.map(recurso => (
            typeof recurso === 'string'
                ? { name: recurso, description: '' }
                : { name: recurso.name, description: recurso.description || '' }
        ));
    };

    // Placeholder del nombre de recurso con el vocabulario del rubro: el primero
    // y el último de los sugeridos, que son los dos más distintos entre sí.
    const getPlaceholderRecurso = () => {
        const sugeridos = getRecursosSugeridos();
        if (sugeridos.length === 0) return 'Ej: Sala 3, Cancha A, Consultorio 1';
        const ejemplos = sugeridos.length === 1
            ? [sugeridos[0].name]
            : [sugeridos[0].name, sugeridos[sugeridos.length - 1].name];
        return `Ej: ${ejemplos.join(', ')}`;
    };

    // Roles típicos del rubro (no personas). Son atajos para abrir el formulario
    // de personal con el puesto ya escrito; el nombre real lo pone el negocio.
    const getRolesSugeridos = () => getCurrentTemplate().suggestedStaffRoles || [];

    /**
     * Cómo se llama, en este rubro, la unidad que se puede reservar: sillón,
     * consultorio, cancha, aula...
     *
     * "Recurso" no le dice nada a nadie. Es lo que define cuántos turnos se
     * pueden tomar a la misma hora, así que la palabra concreta del rubro es la
     * única forma de que se entienda sin explicarlo. El género hace falta para
     * que los botones digan "Una sola" y no "Uno solo" cuando corresponde.
     */
    const getUnidad = () =>
        getCurrentTemplate().unidadReservable ||
        { singular: 'espacio', plural: 'espacios', genero: 'm', nombre: 'Espacio' };

    const esFemenino = () => getUnidad().genero === 'f';
    const cuantos = () => (esFemenino() ? 'Cuántas' : 'Cuántos');
    const unoSolo = () => (esFemenino() ? 'Una sola' : 'Uno solo');
    const masDeUno = () => (esFemenino() ? 'Más de una' : 'Más de uno');

    // La respuesta se deduce de los datos mientras no se haya tocado ningún
    // botón, así volver al paso no obliga a contestar de nuevo.
    const modoRecursosEfectivo =
        modoRecursos ||
        (formData.resources.length > 1 ? 'varios' : formData.resources.length === 1 ? 'uno' : null);

    // Copia los horarios del negocio para usarlos como horario inicial de un
    // recurso: un recurso sin horarios no se puede reservar nunca.
    const getScheduleDelNegocio = () => {
        const schedule = {};
        Object.keys(dayNames).forEach(day => {
            const dia = formData.schedule?.[day];
            const habilitado = !!(dia?.enabled && dia?.ranges?.length > 0);
            schedule[day] = {
                enabled: habilitado,
                ranges: habilitado ? dia.ranges.map(r => ({ ...r })) : []
            };
        });
        return schedule;
    };

    /* Acá vivía getResourceTipMessage: un tip por rubro que se mostraba en un
       cartel naranja ("Si tenés 3 canchas de fútbol 5, creá 3 recursos
       diferentes"). Lo reemplaza la pregunta de dos botones, que dice lo mismo
       sin pedirle a nadie que traduzca "recurso". */

    const getServiceTipMessage = () => {
        if (!formData || !formData.businessType) {
            return { show: false, message: '' };
        }
        const businessType = formData.businessType;
        
        const tipMessages = {
            'sports': {
                show: true,
                message: 'Si tenés 3 canchas de fútbol 5, creá solo 1 servicio "Cancha de Fútbol 5" y después seleccioná las 3 canchas como recursos disponibles para ese servicio.'
            },
            'beauty': {
                show: true,
                message: 'Si tenés 3 sillones para corte de pelo, creá solo 1 servicio "Corte de Pelo" y después seleccioná los 3 sillones como recursos disponibles.'
            },
            'health': {
                show: true,
                message: 'Si tenés 2 consultorios para consultas generales, creá solo 1 servicio "Consulta General" y después seleccioná los 2 consultorios como recursos disponibles.'
            },
            'fitness': {
                show: true,
                message: 'Si das clases de spinning en 2 salas, creá solo 1 servicio "Clase de Spinning" y después seleccioná las 2 salas como recursos disponibles.'
            },
            'education': {
                show: true,
                message: 'Si das clases de inglés en 3 aulas, creá solo 1 servicio "Clase de Inglés" y después seleccioná las 3 aulas como recursos disponibles.'
            },
            'services': {
                show: false, // Los servicios profesionales no suelen tener múltiples recursos
                message: ''
            },
            'other': {
                show: false,
                message: ''
            }
        };

        return tipMessages[businessType] || tipMessages['other'];
    };

    // Step transition handler
    const goToStep = (newStep) => {
        /**
         * `step` tiene agujeros: el 6 (pagos) se eliminó, y el 5 (espacios) no
         * existe en los rubros donde el lugar no es lo que se reserva. Así que
         * lo pedido no siempre es un paso que exista.
         *
         * Se camina en la dirección en la que venías hasta el primer paso real.
         * Antes esto era `if (newStep === 6) finalStep = 7` a secas, y eso
         * rompía el botón Atrás: desde servicios (7) pedía el 6 y lo devolvía al
         * 7, así que no se podía volver.
         */
        let finalStep = newStep;
        if (!pasosVisibles.includes(finalStep)) {
            const haciaAdelante = newStep > step;
            const candidatos = haciaAdelante
                ? pasosVisibles.filter(p => p > step)
                : pasosVisibles.filter(p => p < step);
            finalStep = haciaAdelante ? candidatos[0] : candidatos[candidatos.length - 1];
            // No hay a dónde ir: quedarse donde estaba, sin animar en falso.
            if (finalStep === undefined) return;
        }

        setIsTransitioning(true);
        setTimeout(() => {
            setStep(finalStep);
            setIsTransitioning(false);
        }, 150);
    };

    // Handlers
    const handleTemplateSelect = (template) => {
        setFormData(prev => ({ ...prev, businessType: template.id }));
        // Avanzar automáticamente al siguiente paso con transición
        goToStep(2);
    };

    // Acá estaba handleUserTypeSelect, que además de guardar el tipo de cuenta
    // le creaba al usuario individual un empleado y un recurso por defecto con
    // horario 00:00-23:59. Se eliminó con el paso 0.

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };
    
    const handleScheduleChange = (day, field, value) => {
        setFormData(prev => ({
            ...prev,
            schedule: { ...prev.schedule, [day]: { ...prev.schedule[day], [field]: value } }
        }));
    };

    const handleServiceChange = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            services: prev.services.map((service, i) => 
                i === index ? { ...service, [field]: value } : service
            )
        }));
    };

    const handleAddService = () => {
        const newService = {
            id: Date.now(),
            name: '',
            description: '',
            duration: 60,
            price: 0,
            active: true,
            maxAdvanceBookingDays: 30,
            maxSimultaneousBookings: 1,
            allowWaitingList: false,
            allowCancellation: true,
            requiresDeposit: false,
            staffSelectionMode: 'automatic',
            resourceSelectionMode: 'automatic',
            assignedStaff: [],
            assignedResources: [],
            depositConfig: {
                type: 'percentage',
                amount: 50,
                paymentMethods: formData.payments.methods.length > 0 ? [formData.payments.methods[0]] : ['efectivo'],
                cancellationPolicy: 'flexible'
            },
            priceVariations: [],
            enableDynamicPricing: false
        };
        
        setServiceModalData(newService);
        setEditingServiceIndex(null);
        setShowServiceModal(true);
    };

    const handleRemoveService = (index) => {
        setFormData(prev => ({
            ...prev,
            services: prev.services.filter((_, i) => i !== index)
        }));
    };

    const handleStaffChange = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            staff: prev.staff.map((person, i) => 
                i === index ? { ...person, [field]: value } : person
            )
        }));
    };

    const handleAddStaff = () => {
        setFormData(prev => ({
            ...prev,
            staff: [...prev.staff, { name: '' }]
        }));
    };

    const handleRemoveStaff = (index) => {
        setFormData(prev => ({
            ...prev,
            staff: prev.staff.filter((_, i) => i !== index)
        }));
    };

    // Funciones para manejar recursos
    const handleAddResourceSubmit = () => {
        // Validar el horario antes de guardar
        const scheduleValidation = validateSchedule(newResourceData.schedule);
        if (!scheduleValidation.isValid) {
            alert(scheduleValidation.message);
            return;
        }
        
        const resourceData = {
            ...newResourceData,
            id: Date.now()
        };
        
        if (editingResourceIndex !== null) {
            // Editando recurso existente
            setFormData(prev => ({
                ...prev,
                resources: prev.resources.map((resource, i) => 
                    i === editingResourceIndex ? resourceData : resource
                )
            }));
            setEditingResourceIndex(null);
        } else {
            // Agregando nuevo recurso
            setFormData(prev => ({
                ...prev,
                resources: [...prev.resources, resourceData]
            }));
        }
        
        // Resetear el modal
        setNewResourceData({
            name: '',
            description: '',
            active: true,
            schedule: {
                monday: { enabled: false, ranges: [] },
                tuesday: { enabled: false, ranges: [] },
                wednesday: { enabled: false, ranges: [] },
                thursday: { enabled: false, ranges: [] },
                friday: { enabled: false, ranges: [] },
                saturday: { enabled: false, ranges: [] },
                sunday: { enabled: false, ranges: [] }
            }
        });
        setShowAddResourceModal(false);
    };

    // Carga los recursos sugeridos del rubro con los horarios del negocio.
    // Son solo un punto de partida: se editan y se borran como cualquier recurso.
    const handleCargarRecursosSugeridos = () => {
        setFormData(prev => ({
            ...prev,
            resources: getRecursosSugeridos().map((recurso, index) => ({
                id: Date.now() + index,
                name: recurso.name,
                description: recurso.description,
                active: true,
                // Un schedule propio por recurso, no una referencia compartida
                schedule: getScheduleDelNegocio()
            }))
        }));
    };

    /**
     * "Tengo uno solo": deja un único espacio reservable, con el horario del
     * negocio, y listo. Sin listas, sin modal y sin decidir nada más.
     *
     * Es el caso de la mayoría —una peluquería con un sillón, un consultorio—,
     * y antes tenía que recorrer el mismo camino que un club con ocho canchas:
     * abrir el modal, ponerle nombre, descripción y horarios a algo que es "mi
     * local".
     */
    const elegirUnicoRecurso = () => {
        const unidad = getUnidad();
        setFormData(prev => ({
            ...prev,
            resources: [{
                id: Date.now(),
                name: unidad.nombre,
                description: '',
                active: true,
                schedule: getScheduleDelNegocio()
            }]
        }));
        setModoRecursos('uno');
    };

    const handleRemoveResource = (index) => {
        setFormData(prev => ({
            ...prev,
            resources: prev.resources.filter((_, i) => i !== index)
        }));
    };

    const handleResourceScheduleChange = (day, field, value) => {
        setNewResourceData(prev => ({
            ...prev,
            schedule: {
                ...prev.schedule,
                [day]: {
                    ...prev.schedule[day],
                    [field]: value,
                    // Si se está activando el día y no hay rangos, usar horarios del negocio
                    ranges: field === 'enabled' && value && prev.schedule[day].ranges.length === 0 
                        ? getBusinessHoursForDay(day)
                        : prev.schedule[day].ranges
                }
            }
        }));
    };

    const addResourceTimeRange = (day) => {
        const dayRanges = newResourceData.schedule[day].ranges;
        let newStartTime = '09:00';
        let newEndTime = '17:00';
        
        if (dayRanges.length > 0) {
            const lastRange = dayRanges[dayRanges.length - 1];
            newStartTime = lastRange.end;
            const [hours, minutes] = lastRange.end.split(':');
            let endHour = parseInt(hours) + 2;
            if (endHour >= 24) {
                newEndTime = '23:59';
            } else {
                newEndTime = `${endHour.toString().padStart(2, '0')}:${minutes}`;
            }
        }
        
        setNewResourceData(prev => ({
            ...prev,
            schedule: {
                ...prev.schedule,
                [day]: {
                    ...prev.schedule[day],
                    ranges: [...prev.schedule[day].ranges, { start: newStartTime, end: newEndTime }]
                }
            }
        }));
    };

    const removeResourceTimeRange = (day, rangeIndex) => {
        setNewResourceData(prev => ({
            ...prev,
            schedule: {
                ...prev.schedule,
                [day]: {
                    ...prev.schedule[day],
                    ranges: prev.schedule[day].ranges.filter((_, i) => i !== rangeIndex)
                }
            }
        }));
    };

    const updateResourceTimeRange = (day, rangeIndex, field, value) => {
        setNewResourceData(prev => ({
            ...prev,
            schedule: {
                ...prev.schedule,
                [day]: {
                    ...prev.schedule[day],
                    ranges: prev.schedule[day].ranges.map((range, i) => 
                        i === rangeIndex ? { ...range, [field]: value } : range
                    )
                }
            }
        }));
    };

    const handlePaymentsChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (type === 'checkbox') {
            const method = name;
        setFormData(prev => ({
            ...prev,
            payments: {
                ...prev.payments,
                    methods: checked 
                        ? [...prev.payments.methods, method]
                        : prev.payments.methods.filter(m => m !== method)
            }
        }));
        } else {
            setFormData(prev => ({
                ...prev,
                payments: { ...prev.payments, [name]: value }
            }));
        }
    };

    const handlePaymentsMethodChange = (e) => {
        const { name, checked } = e.target;
        setFormData(prev => ({
                ...prev,
                payments: {
                    ...prev.payments,
                methods: checked 
                    ? [...prev.payments.methods, name]
                    : prev.payments.methods.filter(method => method !== name)
                }
        }));
    };

    const handleIntegrationChange = (integrationName, checked) => {
        setFormData(prev => ({
            ...prev,
            integrations: {
                ...prev.integrations,
                [integrationName]: checked
            }
        }));
    };

    /* Acá vivía `handleLogoChange`, que leía el archivo como data URL (base64) y
       lo metía en `branding.logo`. No lo llamaba ningún input: quedó suelto
       cuando se sacó el modal de marca viejo. Lo reemplaza SelectorDeImagen en
       el paso 8, que sube a Storage y guarda la URL —una foto en base64 se
       guardaría entera adentro del documento del negocio y viajaría en cada
       lectura de la página pública—. */

    /* handleBrandingChange se eliminó junto con el modal de colores: el selector
       del paso escribe el branding entero, así que no hace falta un puente que
       lo mezcle con el anterior. */

    // Funciones para integraciones (copiado del dashboard)
    const toggleIntegration = (integrationKey) => {
        setIntegrations(prev => ({
            ...prev,
            [integrationKey]: {
                ...prev[integrationKey],
                active: !prev[integrationKey].active
            }
        }));
    };

    // Conectar Google Calendar con OAuth2 real
    const handleGoogleCalendarConnect = async () => {
        try {
            const token = await currentUser.getIdToken();
            const businessId = currentUser.uid;
            
            // Solicitar URL de autorización al backend
            const response = await fetch(`${API_URL}/api/oauth/google/auth?state=${businessId}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            if (!response.ok) {
                throw new Error('Error al obtener URL de autorización');
            }
            
            const { authUrl } = await response.json();
            
            // Abrir ventana de OAuth
            const authWindow = window.open(authUrl, 'google-oauth', 'width=500,height=600');
            
            // Escuchar el resultado
            return new Promise((resolve, reject) => {
                const handleMessage = (event) => {
                    if (event.data === 'google_calendar_connected') {
                        window.removeEventListener('message', handleMessage);
                        authWindow.close();
                        
                        // Actualizar estado
                        setIntegrations(prev => ({
                            ...prev,
                            googleCalendar: { ...prev.googleCalendar, connected: true }
                        }));
                        
                        alert('¡Google Calendar conectado exitosamente!');
                        resolve();
                    } else if (event.data === 'google_calendar_error') {
                        window.removeEventListener('message', handleMessage);
                        authWindow.close();
                        reject(new Error('Error al conectar Google Calendar'));
                    }
                };
                
                // Timeout si la ventana se cierra manualmente
                const timeoutId = setTimeout(() => {
                    window.removeEventListener('message', enhancedHandleMessage);
                    reject(new Error('Proceso de autorización cancelado o timeout'));
                }, 60000);
                
                const enhancedHandleMessage = (event) => {
                    clearTimeout(timeoutId);
                    handleMessage(event);
                };
                
                window.addEventListener('message', enhancedHandleMessage);
            });
            
        } catch (error) {
            throw new Error(`Error en OAuth Google: ${error.message}`);
        }
    };

    const handleConnect = async (integrationKey) => {
        // Iniciar proceso de conexión
        setConnectingIntegration(integrationKey);
        
        try {
            // Lógica específica para Google Calendar
            if (integrationKey === 'googleCalendar') {
                await handleGoogleCalendarConnect();
            }
            // Lógica específica para Mercado Pago OAuth
            else if (integrationKey === 'mercadoPago') {
                // Starting MercadoPago OAuth connection
                
                // Construir URL de autorización de Mercado Pago
                const authParams = new URLSearchParams({
                    response_type: 'code',
                    client_id: MERCADO_PAGO_CONFIG.CLIENT_ID,
                    redirect_uri: MERCADO_PAGO_CONFIG.REDIRECT_URI
                });
                
                const authUrl = `${MERCADO_PAGO_CONFIG.AUTH_URL}?${authParams.toString()}`;
                
                // Redirecting to MercadoPago
                
                // Redireccionar al usuario a Mercado Pago para autorización
                window.location.href = authUrl;
                
                // Nota: El flujo continúa en el endpoint /callback del backend
                return; // No ejecutar el resto del código
            }
            else {
                // Otras integraciones futuras
                throw new Error('Integración no implementada');
            }
            
        } catch (error) {
            console.error('Error en handleConnect:', error);
            alert(`Error al conectar con ${getIntegrationName(integrationKey)}: ${error.message}`);
        } finally {
            setConnectingIntegration(null);
        }
    };

    const handleDisconnect = (integrationKey) => {
        setIntegrations(prev => ({
            ...prev,
            [integrationKey]: {
                ...prev[integrationKey],
                connected: false,
                active: false
            }
        }));
    };

    const getIntegrationName = (key) => {
        const names = {
            googleCalendar: "Google Calendar",
            mercadoPago: "Mercado Pago"
        };
        return names[key] || key;
    };

    const handleEditService = (serviceIndex) => {
        const service = formData.services[serviceIndex];
        setServiceModalData(service);
        setEditingServiceIndex(serviceIndex);
        setShowServiceModal(true);
    };

    const handleSaveService = (serviceData) => {
        // Auto-asignar staff y recursos para usuarios individuales
        let finalServiceData = { ...serviceData };
        
        if (formData.userType === 'individual') {
            // Auto-asignar el único staff disponible
            if (formData.staff.length > 0) {
                finalServiceData.assignedStaff = [formData.staff[0].id];
            }
            
            // Auto-asignar el único recurso disponible
            if (formData.resources.length > 0) {
                finalServiceData.assignedResources = [formData.resources[0].id];
            }
        }
        
        if (editingServiceIndex !== null) {
            // Editando servicio existente
            setFormData(prev => ({
                ...prev,
                services: prev.services.map((service, index) => 
                    index === editingServiceIndex ? finalServiceData : service
                )
            }));
        } else {
            // Agregando nuevo servicio
            setFormData(prev => ({
                ...prev,
                services: [...prev.services, finalServiceData]
            }));
        }
        
        setShowServiceModal(false);
        setServiceModalData(null);
        setEditingServiceIndex(null);
    };

    const handleDeleteService = (serviceIndex) => {
        if (window.confirm('¿Estás seguro de que quieres eliminar este servicio?')) {
            setFormData(prev => ({
                ...prev,
                services: prev.services.filter((_, index) => index !== serviceIndex)
            }));
        }
    };

    const handleCancelServiceModal = () => {
        setShowServiceModal(false);
        setServiceModalData(null);
        setEditingServiceIndex(null);
    };

    // Logos simples y limpios
    const GoogleLogo = ({ isConnected }) => (
        <svg width="20" height="20" viewBox="0 0 24 24">
            <path fill={isConnected ? "white" : "#4285F4"} d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill={isConnected ? "white" : "#34A853"} d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill={isConnected ? "white" : "#FBBC05"} d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill={isConnected ? "white" : "#EA4335"} d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
    );

    const MercadoPagoLogo = ({ isConnected }) => (
        <img 
            src={mercadoPagoLogo} 
            alt="Mercado Pago" 
            style={{ 
                width: '24px', 
                height: '24px',
                opacity: isConnected ? 1 : 0.7,
                filter: isConnected ? 'none' : 'grayscale(30%)'
            }} 
        />
    );

    const availableIntegrations = [
        {
            key: "googleCalendar",
            name: "Google Calendar",
            description: "Sincroniza tu agenda automáticamente",
            icon: <GoogleLogo isConnected={false} />,
            color: "#4285F4",
            category: "Calendario",
            benefits: ["Sincronización automática", "Recordatorios", "Disponibilidad en tiempo real"]
        },
        {
            key: "mercadoPago",
            name: "Mercado Pago",
            description: "Cobrá la seña del turno con tu propia cuenta",
            icon: <MercadoPagoLogo isConnected={false} />,
            color: "#009EE3",
            category: "Pagos",
            benefits: ["Cobro de señas", "Tarjeta, débito y dinero en cuenta", "La plata va a tu cuenta"]
        },
        // Las que todavía no están implementadas se muestran con `proximamente`,
        // que en la tarjeta reemplaza el estado y deshabilita el botón. No se
        // pintan como disponibles a propósito: una integración que se puede
        // "conectar" y no hace nada es peor que una que avisa que falta.
        {
            key: "transferencia",
            name: "Transferencia bancaria",
            description: "Que te transfieran la seña a tu cuenta",
            icon: <Landmark size={22} strokeWidth={2} />,
            color: "#2E7D32",
            category: "Pagos",
            benefits: ["Sin comisiones", "Se acredita directo", "Mostrás tu CBU o alias"],
            proximamente: true
        },
        {
            key: "paypal",
            name: "PayPal",
            description: "Cobrá a clientes del exterior",
            icon: <Wallet size={22} strokeWidth={2} />,
            color: "#003087",
            category: "Pagos",
            benefits: ["Pagos internacionales", "Múltiples monedas", "Protección al comprador"],
            proximamente: true
        },
        {
            key: "talo",
            name: "Talo",
            description: "Cobros con transferencias y cripto",
            icon: <Zap size={22} strokeWidth={2} />,
            color: "#111111",
            category: "Pagos",
            benefits: ["Acreditación inmediata", "Comisiones bajas", "Link de pago"],
            proximamente: true
        }
    ];

    // Función para manejar el flujo de pago con MercadoPago
    const handlePaymentFlow = async () => {
        try {
            const token = await currentUser.getIdToken();
            
            // Encontrar los datos del tier seleccionado
            // El plan que eligió el usuario. Antes acá había una tabla con los
            // seis planes y sus precios escritos a mano —$440 para el plan de
            // $34.999, entre otros—: era la quinta copia del catálogo y la más
            // desactualizada de todas.
            //
            // Se puede borrar entera porque el backend ya no acepta el monto ni
            // el nombre del pedido: los saca de src/planes.js a partir del
            // planId. Ver el ítem [24a]. Lo único que hay que mandar es qué
            // plan se quiere.
            if (!formData.selectedPlan) {
                throw new Error('Plan seleccionado no encontrado');
            }

            // Guardar datos del onboarding en localStorage para después del pago
            localStorage.setItem('pendingOnboardingData', JSON.stringify({
                formData,
                integrations,
                timestamp: Date.now()
            }));

            // URLs de retorno - Usar la URL actual del onboarding
            const currentUrl = window.location.origin;
            const returnUrl = `${currentUrl}/onboarding?payment=success`;
            const cancelUrl = `${currentUrl}/onboarding?payment=cancelled`;

            // Crear suscripción recurrente en MercadoPago  
            const paymentResponse = await fetch(`${API_URL}/api/subscriptions/create-preapproval`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    // Sólo el plan: el precio, el nombre y la URL del webhook
                    // los pone el backend desde src/planes.js. Mandarlos desde
                    // acá era lo que permitía pedir un plan caro a precio de
                    // uno barato (ítem [24a]).
                    planId: formData.selectedPlan,
                    billingCycle: formData.selectedBillingCycle || 'monthly',
                    userEmail: currentUser.email,
                    returnUrl,
                    cancelUrl,
                    isOnboarding: true // Flag para indicar que viene del onboarding
                }),
            });

            if (paymentResponse.ok) {
                const paymentData = await paymentResponse.json();
                
                if (paymentData.initPoint) {
                    // Redirigir a MercadoPago
                    window.location.href = paymentData.initPoint;
                } else {
                    throw new Error('No se recibió URL de pago de MercadoPago');
                }
            } else {
                const error = await paymentResponse.json();
                throw new Error(error.message || 'Error al crear el pago');
            }
            
        } catch (error) {
            console.error('Error en flujo de pago:', error);
            throw error;
        }
    };

    const handleFinish = async () => {
        setLoading(true);
        setError('');
        
        try {
                    // Starting completion process
            
            // Validar datos antes de enviar
            if (!formData.businessType) {
                throw new Error('Tipo de negocio es requerido');
            }
            if (!formData.businessInfo.name || !formData.businessInfo.email) {
                throw new Error('Nombre y email del negocio son requeridos');
            }
            if (!formData.selectedPlan) {
                throw new Error('Debes seleccionar un plan para continuar');
            }
            /* Acá había un comentario que decía "los términos ya fueron
               aceptados en el registro", y por eso no se volvían a pedir.

               Es cierto pero no alcanza: en el registro se acepta para tener un
               usuario; en este paso se crea el negocio y se contrata un plan que
               se cobra todos los meses. Es un compromiso distinto, y el momento
               de decirlo es antes de apretar el botón que lo activa, no diez
               pasos antes cuando todavía no se sabía qué plan iba a elegirse. */
            if (!aceptaLegales) {
                throw new Error('Para crear tu negocio tenés que aceptar los términos y la política de privacidad');
            }

            if (!currentUser) {
                throw new Error('Usuario no autenticado');
            }

            // Si es plan de pago (no trial), crear pago primero
            if (!formData.isTrialPlan) {
                await handlePaymentFlow();
                return; // La función handlePaymentFlow redirigirá a MercadoPago
            }

            // Auto-generar staff y recursos para personas individuales
            let finalFormData = { ...formData };
            if (formData.userType === 'individual') {
                // Auto-generar staff usando los horarios del negocio
                finalFormData.staff = [{
                    id: 1,
                    name: formData.businessInfo.name || 'Propietario',
                    email: formData.businessInfo.email || '',
                    phone: formData.businessInfo.phone || '',
                    active: true,
                    schedule: { ...formData.schedule } // Usar los mismos horarios del negocio
                }];

                // Auto-generar recurso usando los horarios del negocio
                finalFormData.resources = [{
                    id: 1,
                    name: 'Espacio principal',
                    description: 'Área de trabajo principal',
                    active: true,
                    schedule: { ...formData.schedule } // Usar los mismos horarios del negocio
                }];
            }

            const businessData = {
                ...finalFormData,
                integrations: integrations,
                userId: currentUser.uid,
                createdAt: new Date().toISOString(),
                isActive: true,
                // Queda registrado CUÁNDO y QUÉ VERSIÓN se aceptó, no sólo que
                // se tildó una casilla. Una aceptación que no se guarda no
                // prueba nada el día que hay que demostrarla, y sin la versión
                // no se sabe qué texto era el que estaba a la vista.
                aceptacionLegal: {
                    aceptadoEn: new Date().toISOString(),
                    version: ULTIMA_ACTUALIZACION
                }
            };

            // Debug: Log the data being sent
            import('../utils/debug/debug-onboarding.js').then(({ debugOnboardingData }) => {
                debugOnboardingData.logOnboardingData(businessData);
            });
            
            // Debug: Log schedule data specifically
            import('../utils/debug/debug-schedule.js').then(({ debugSchedule }) => {
                debugSchedule.logScheduleData('ONBOARDING_SEND', businessData.schedule, 'Data being sent to backend');
                debugSchedule.validateScheduleStructure(businessData.schedule);
            });

            // Sending business data

            // Obtener token de autenticación
            // Getting authentication token
            const token = await currentUser.getIdToken();

            // Usar variable de entorno para la URL de la API con fallback
            const apiUrl = API_URL;
            const endpoint = `${apiUrl}/api/business`;
            // Making API request
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(businessData)
            });

                    // Server response received

            if (!response.ok) {
                let errorMessage = 'Error al crear el negocio';
                
                try {
                const errorData = await response.json();
                    console.error('❌ FRONTEND: Error del servidor:', errorData);
                    errorMessage = errorData.error || errorData.message || errorMessage;
                    
                    // Mostrar detalles adicionales si están disponibles
                    if (errorData.details) {
                        console.error('❌ FRONTEND: Detalles del error:', errorData.details);
                    }
                } catch (parseError) {
                    console.error('❌ FRONTEND: Error al parsear respuesta de error:', parseError);
                    
                    try {
                        // Si no se puede parsear como JSON, podría ser HTML (página de error)
                        const textResponse = await response.text();
                        console.error('❌ FRONTEND: Respuesta como texto:', textResponse.substring(0, 500));
                        
                        if (textResponse.includes('<!DOCTYPE')) {
                            errorMessage = 'El servidor devolvió una página HTML en lugar de datos JSON. Verifica que el endpoint esté configurado correctamente.';
                        } else if (textResponse.includes('Cannot GET') || textResponse.includes('Cannot POST')) {
                            errorMessage = 'El endpoint no existe en el servidor. Verifica la configuración del backend.';
                        } else if (response.status === 404) {
                            errorMessage = 'Endpoint no encontrado (404). El servidor está corriendo pero el endpoint /api/business no existe.';
                        } else if (response.status === 401) {
                            errorMessage = 'Error de autenticación. El token puede ser inválido.';
                        } else if (response.status === 500) {
                            errorMessage = 'Error interno del servidor. Revisa los logs del backend.';
                        }
                    } catch (textError) {
                        console.error('❌ FRONTEND: Error al leer respuesta como texto:', textError);
                        errorMessage = `Error ${response.status}: No se pudo leer la respuesta del servidor`;
                    }
                }
                
                throw new Error(errorMessage);
            }

            const result = await response.json();
            // Business created successfully

            // Mostrar mensaje de éxito temporal
            setError('');
            // Redirecting to dashboard

            // Actualizar el estado del usuario para reflejar que completó el onboarding
            // Updating user data
            
            // Forzar una actualización de los datos del usuario desde el AuthContext
            // Esto es importante para que ProtectedRoute sepa que el onboarding se completó
            if (typeof refreshUserData === 'function') {
                await refreshUserData();
                // User data updated
            }

            // Redirigir al dashboard
            navigate('/admin/dashboard');
            
        } catch (error) {
            console.error('❌ FRONTEND: Error en handleFinish:', error);
            console.error('❌ FRONTEND: Stack trace:', error.stack);
            
            let userFriendlyMessage = error.message;
            
            // Personalizar mensajes de error comunes
            if (error.message.includes('Failed to fetch')) {
                userFriendlyMessage = 'No se pudo conectar con el servidor. Verifica que el backend esté ejecutándose en http://localhost:3001';
            } else if (error.message.includes('NetworkError')) {
                userFriendlyMessage = 'Error de conexión. Verifica tu conexión a internet.';
            } else if (error.message.includes('not available')) {
                userFriendlyMessage = 'El servidor no está disponible. Intenta nuevamente en unos minutos.';
            } else if (error.message.includes('body stream already read')) {
                userFriendlyMessage = 'Error de comunicación con el servidor. Intenta nuevamente.';
            }
            
            setError(userFriendlyMessage);
        } finally {
            setLoading(false);
        }
    };
    
    const canGoToNextStep = () => {
        if (!formData) return false;
        
        if (step === 1) {
            return formData.businessType !== '';
        }
        if (step === 2) {
            return formData.businessInfo.name.trim() !== '' && formData.businessInfo.email.trim() !== '';
        }
        if (step === 3) {
            const canGo = Object.values(formData.schedule).some(day => day.enabled && day.ranges.length > 0);
            // Step 3 validation
            return canGo;
        }
        if (step === 4) {
            // Personal: obligatorio. Antes se salteaba para cuentas individuales.
            const canGo = formData.staff.length > 0 && formData.staff.some(person => person.active);
            // Step 4 validation
            return canGo;
        }
        if (step === 5) {
            // Recursos: obligatorio. Antes se salteaba para cuentas individuales.
            const canGo = formData.resources.length > 0 && formData.resources.some(resource => resource.active);
            // Step 5 validation
            return canGo;
        }
        // Paso 6 eliminado - configuración de pagos se hace después
        if (step === 7) {
            // Servicios - verificar si hay al menos un servicio completo
            return formData.services.length > 0 && formData.services.some(service => 
                service.name && service.name.trim() !== '' && service.duration > 0 && service.price > 0
            );
        }
        if (step === 8) {
            // Branding - no es obligatorio, siempre puede continuar
            return true;
        }
        if (step === 9) {
            // Integraciones - no es obligatorio, siempre puede continuar
            return true;
        }
        if (step === 10) {
            // Selección de plan - debe elegir un plan
            return formData.selectedPlan !== '';
        }
        return true;
    };
    
    // DEBUG: Monitor step and userType changes
    useEffect(() => {
        if (formData) {
            // Step change monitoring
        }
    }, [step, formData?.userType]);

    // Función para manejar pago exitoso
    const handlePaymentSuccess = async () => {
        try {
            // Recuperar datos del onboarding desde localStorage
            const pendingData = localStorage.getItem('pendingOnboardingData');
            if (!pendingData) {
                throw new Error('No se encontraron datos del onboarding pendientes');
            }

            const { formData: savedFormData, integrations: savedIntegrations } = JSON.parse(pendingData);
            
            // Restaurar los datos
            setFormData(savedFormData);
            setIntegrations(savedIntegrations);

            // Crear el negocio con el plan pagado
            await createBusinessWithPaidPlan(savedFormData, savedIntegrations);
            
            // Limpiar localStorage
            localStorage.removeItem('pendingOnboardingData');
            
            // Limpiar URL
            const cleanUrl = window.location.pathname;
            window.history.replaceState({}, document.title, cleanUrl);

        } catch (error) {
            console.error('Error al procesar pago exitoso:', error);
            setError(`Error al completar el proceso: ${error.message}`);
        }
    };

    // Función para manejar pago cancelado
    const handlePaymentCancelled = () => {
        // Limpiar URL
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
        
        // Mostrar mensaje al usuario
        setError('Pago cancelado. Puedes intentar nuevamente o elegir la opción de prueba gratuita si está disponible.');
        setLoading(false);
    };

    // Función para crear negocio con plan pagado
    const createBusinessWithPaidPlan = async (savedFormData, savedIntegrations) => {
        // Auto-generar staff y recursos para personas individuales
        let finalFormData = { ...savedFormData };
        if (savedFormData.userType === 'individual') {
            // Auto-generar staff usando los horarios del negocio
            finalFormData.staff = [{
                id: 1,
                name: savedFormData.businessInfo.name || 'Propietario',
                email: savedFormData.businessInfo.email || '',
                phone: savedFormData.businessInfo.phone || '',
                active: true,
                schedule: { ...savedFormData.schedule }
            }];

            // Auto-generar recurso usando los horarios del negocio
            finalFormData.resources = [{
                id: 1,
                name: 'Espacio principal',
                description: 'Área de trabajo principal',
                active: true,
                schedule: { ...savedFormData.schedule }
            }];
        }

        const businessData = {
            ...finalFormData,
            integrations: savedIntegrations,
            // La imagen viaja suelta, además de adentro de `branding`, porque el
            // backend la guarda en la raíz del documento: es `profileImage ||
            // logo` de la raíz lo que lee la página pública, y de ahí sale el
            // og:image al compartir el link. Mandada sólo adentro de `branding`
            // se guardaba igual, pero en un lugar que no lee nadie: la foto se
            // subía, se veía en la vista previa del paso, y la página terminada
            // salía con las iniciales.
            logo: finalFormData.branding?.logo || null,
            userId: currentUser.uid,
            createdAt: new Date().toISOString(),
            isActive: true,
            // El otro camino: el negocio se crea al volver del pago del plan.
            // La aceptación se registra igual — se dio antes de ir a pagar, en
            // el mismo botón. Si sólo estuviera en el camino de la prueba
            // gratuita, justo los que pagan quedarían sin constancia.
            aceptacionLegal: {
                aceptadoEn: new Date().toISOString(),
                version: ULTIMA_ACTUALIZACION
            }
        };

        const token = await currentUser.getIdToken();
        // La ruta es POST /api/business (server.js:7122). Acá decía
        // /api/business/create, que no existe en el backend: el último paso del
        // onboarding devolvía 404 y tiraba "Error al crear el negocio", así que
        // no se podía terminar de configurar un negocio.
        const response = await fetch(`${API_URL}/api/business`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(businessData),
        });

        if (!response.ok) {
            let errorMessage = 'Error al crear el negocio';
            try {
                const errorData = await response.json();
                errorMessage = errorData.error || errorData.message || errorMessage;
            } catch (e) {
                console.error('Error parsing error response');
            }
            throw new Error(errorMessage);
        }

        const result = await response.json();
        
        // Refrescar datos del usuario
        if (typeof refreshUserData === 'function') {
            await refreshUserData();
        }

        // Redirigir al dashboard
        navigate('/admin/dashboard');
    };

    // Manejar callback de MercadoPago OAuth y pagos
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const mercadoPagoStatus = urlParams.get('mercadopago');
        const userId = urlParams.get('user_id');
        const errorMessage = urlParams.get('message');
        const paymentStatus = urlParams.get('payment');
        
        // Manejar retorno de pago de suscripción
        if (paymentStatus === 'success') {
            handlePaymentSuccess();
        } else if (paymentStatus === 'cancelled') {
            handlePaymentCancelled();
        } 
        // Manejar OAuth de MercadoPago (integración)
        else if (mercadoPagoStatus === 'connected' && userId) {
            // MercadoPago connected successfully
            
            // Actualizar estado de integración
            setIntegrations(prev => ({
                ...prev,
                mercadoPago: { 
                    ...prev.mercadoPago, 
                    connected: true,
                    user_id: userId,
                    connected_at: new Date().toISOString()
                }
            }));
            
            // Limpiar URL sin recargar la página
            const cleanUrl = window.location.pathname;
            window.history.replaceState({}, document.title, cleanUrl);
            
            // TODO: Mostrar notificación de éxito más elegante
            // MercadoPago connection successful
            
        } else if (mercadoPagoStatus === 'error') {
            console.error('❌ Error al conectar MercadoPago:', errorMessage);
            
            // Limpiar URL
            const cleanUrl = window.location.pathname;
            window.history.replaceState({}, document.title, cleanUrl);
            
            // TODO: Mostrar notificación de error más elegante
            setError(`Error al conectar con MercadoPago: ${errorMessage || 'Error desconocido'}`);
        }
    }, []); // Solo ejecutar una vez al montar el componente
    
    return (
        <>
            {!formData ? (
                <div className="onboarding-container">
                    <div className="loading-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                        <div>Cargando...</div>
                    </div>
                </div>
            ) : (
        <div className="onboarding-container">
            <div className="onboarding-progress">
                <div className="progress-header">
                    <h1>Configurá tu negocio</h1>
                    <span>Paso {posicionDelPaso} de {pasosVisibles.length}</span>
                </div>
                <div className="progress-bar-container">
                    <div
                        className="progress-bar"
                        style={{ '--progress': `${(posicionDelPaso / pasosVisibles.length) * 100}%` }}
                    ></div>
                </div>
            </div>

            {/* Sin la clase `container`: este div ya define su propio ancho y sus
                márgenes automáticos en .onboarding-content. Con las dos, el mismo
                elemento recibía tres max-width distintos —1600px de acá, 1200px
                del .container de index.css y 1400px del container de Tailwind—,
                los tres con la misma especificidad, así que ganaba el que quedara
                último en el bundle y la caja terminaba corrida respecto del
                centro de la pantalla. */}
            <div className="onboarding-content">
                {error && <p className="error-message" style={{textAlign: 'center', marginBottom: '2rem'}}>{error}</p>}
                
                {/* Acá estaba el paso 0, que preguntaba si eras una persona
                    individual o un negocio. Se eliminó: el plan personal se
                    discontinuó, así que la respuesta era siempre "negocio" y el
                    paso solo alargaba el onboarding. */}

                {/* Paso 1: Selección de tipo de negocio */}
                {step === 1 && (
                    <div className={`onboarding-step ${isTransitioning ? 'transitioning' : ''}`}>
                        <h2>¿Qué tipo de negocio tenés?</h2>
                        <p>Elegí la categoría que mejor describa tu negocio para personalizar tu experiencia.</p>
                        <div className="templates-grid">
                            {/* Los rubros concretos van en la grilla, y "Otro" abajo,
                                aparte y a lo ancho.
                                Son 7 rubros y la grilla era de 4 columnas: quedaban
                                4 + 3 y un hueco vacío en la segunda fila. Con 6 en
                                una grilla de 3 la cuadrícula cierra, y "Otro" gana
                                lo que además le corresponde: no es un rubro más, es
                                el escape cuando ninguno encaja, y mezclado entre los
                                demás parecía una opción equivalente. */}
                            {rubrosConcretos.map(template => (
                                <div key={template.id} onClick={() => handleTemplateSelect(template)} className={`template-card ${formData.businessType === template.id ? 'selected' : ''}`}>
                                    <div className="template-icon">{template.icon}</div>
                                    <h3>{template.name}</h3>
                                    <p>{template.description}</p>
                                </div>
                            ))}

                            {/* "Otro" va DENTRO de la grilla, ocupando las tres
                                columnas. Estaba afuera, como hermano, y la grilla
                                tiene su propio panel con padding: el botón quedaba
                                de punta a punta mientras las tarjetas estaban
                                metidas hacia adentro, así que sus bordes no
                                coincidían con los de la primera ni la última
                                columna. */}
                            {rubroOtro && (
                                <button
                                    type="button"
                                    onClick={() => handleTemplateSelect(rubroOtro)}
                                    className={`template-card template-card-otro ${formData.businessType === rubroOtro.id ? 'selected' : ''}`}
                                >
                                    <span className="template-icon">{rubroOtro.icon}</span>
                                    <span className="template-otro-texto">
                                        <strong>{rubroOtro.name}</strong>
                                        <span>{rubroOtro.description}</span>
                                    </span>
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {/* Paso 2: Datos básicos */}
                {step === 2 && (
                    <div className={`onboarding-step ${isTransitioning ? 'transitioning' : ''}`}>
                        <h2>Datos básicos del negocio</h2>
                        <p>Contanos sobre tu negocio para crear tu página personalizada.</p>
                        {/* El botón al pie de la última parte hace lo mismo
                            que la flecha de abajo, y va por lo que ya hacía
                            falta en el detalle de servicio: acá adentro las
                            partes se cierran y se abren solas, y quien termina
                            de completar la última no tiene por qué volver a
                            buscar la flecha al pie de la página para seguir.
                            Se apaga con el mismo criterio que la flecha
                            —canGoToNextStep()— para que no haya dos formas de
                            avanzar que no coincidan. */}
                        <BusinessInfoForm
                            value={formData.businessInfo}
                            onChange={val => setFormData(prev => ({ ...prev, businessInfo: val }))}
                            businessType={formData.businessType}
                            onSiguiente={() => goToStep(step + 1)}
                            puedeAvanzar={canGoToNextStep() && !isTransitioning}
                            motivoBloqueo="Completá el nombre y el email para continuar"
                        />
                    </div>
                )}

                {/* Paso 3: Horarios de atención */}
                {step === 3 && (
                    <div className={`onboarding-step ${isTransitioning ? 'transitioning' : ''}`}>
                        <h2>Horarios de atención</h2>
                        {/* La bajada la pone la propia grilla, que además
                            explica cómo se usa. Acá había otra que solo repetía
                            el título con más palabras ("Configura los días y
                            horarios en los que tu negocio está abierto"): dos
                            párrafos seguidos diciendo lo mismo, 60px de alto en
                            un celular antes de llegar a lo que hay que hacer. */}
                        <BusinessScheduleForm
                            value={formData.schedule}
                            onChange={schedule => setFormData(prev => ({ ...prev, schedule }))}
                            hours={hours}
                            minutes={minutes}
                            dayNames={dayNames}
                        />
                    </div>
                )}

                {/* Paso 4: Personal */}
                {step === 4 && (
                    <div className={`onboarding-step ${isTransitioning ? 'transitioning' : ''}`}>
                        <h2>{getCurrentTemplate().staffTitle}</h2>
                        <p>{getCurrentTemplate().staffDescription}</p>
                        
                        {/* Mensaje de obligatoriedad - Solo mostrar si no hay personal activo */}
                        {(formData.staff.length === 0 || !formData.staff.some(person => person.active)) && (
                            <div style={{ 
                                background: 'rgba(220, 38, 38, 0.1)', 
                                border: '1px solid rgba(220, 38, 38, 0.2)', 
                                borderRadius: '8px', 
                                padding: '1rem', 
                                marginBottom: '1.5rem',
                                textAlign: 'center'
                            }}>
                                <p style={{ 
                                    margin: 0, 
                                    fontSize: '0.875rem', 
                                    color: 'var(--gray-700)',
                                    fontWeight: '600'
                                }}>
                                    ⚠️ <strong>Obligatorio:</strong> Agregá al menos una persona para continuar
                                </p>
                            </div>
                        )}
                        
                        {/* Mensaje de éxito cuando ya hay personal */}
                        {formData.staff.length > 0 && formData.staff.some(person => person.active) && (
                            <div style={{ 
                                background: 'rgba(34, 197, 94, 0.1)', 
                                border: '1px solid rgba(34, 197, 94, 0.2)', 
                                borderRadius: '8px', 
                                padding: '1rem', 
                                marginBottom: '1.5rem',
                                textAlign: 'center'
                            }}>
                                <p style={{ 
                                    margin: 0, 
                                    fontSize: '0.875rem', 
                                    color: 'var(--gray-700)',
                                    fontWeight: '600'
                                }}>
                                    ✅ <strong>Listo:</strong> ya podés seguir al paso siguiente.
                                </p>
                            </div>
                        )}
                        
                        <div style={{ marginBottom: '1.5rem' }}>
                            <h3 style={{ margin: '0 0 1rem 0', color: 'var(--gray-900)', fontSize: '1.25rem', fontWeight: '700', textAlign: 'center' }}>
                                👥 Personal ({formData.staff.filter(s => s.active).length}/{formData.staff.length})
                            </h3>
                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <button
                                    className="btn-primary btn-large-centered"
                                onClick={() => {
                                    setNuevoStaff({ ...staffVacio, schedule: JSON.parse(JSON.stringify(formData.schedule)) });
                                    setErrorStaff('');
                                    setShowNewStaffForm(true);
                                }}
                            >
                                + Agregar Personal
                            </button>
                            </div>

                            {/* Acá había una fila de atajos con los roles del rubro
                                ("+ Estilista", "+ Colorista"). Se eliminaron: escribían
                                el rol en el campo Nombre, así que daban de alta a una
                                persona llamada "Estilista". El rol ahora es un campo
                                propio dentro del formulario. */}
                        </div>

                        <div className="grilla-de-personal">
                                                    {formData.staff.map((person, idx) => (
                        <div key={person.id || idx} className={`service-card ${!person.active ? 'inactive' : ''}`}>
                            <div className="service-header">
                                <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                    <h4 style={{ margin: '0 0 0.25rem 0', fontSize: '1rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                                        {/* La foto es opcional: sin ella no va nada, no un
                                            recuadro vacío que ocupe el lugar. */}
                                        {person.foto && (
                                            <img src={person.foto} alt="" className="foto-de-persona" />
                                        )}
                                        {person.name}
                                        {/* Un chip por rol y no el texto junto: con dos roles
                                            "Cardiólogo, Nutricionista" se lee como un puesto
                                            largo en vez de como dos. */}
                                        {rolesDe(person).map((rol) => (
                                            <span key={rol} className="etiqueta-de-rol">
                                                {rol}
                                            </span>
                                        ))}
                                    </h4>
                                    {/* Con el punto suelto: si no hay mail ni teléfono
                                        —los dos son opcionales— quedaba un "•" solitario
                                        colgando debajo del nombre. */}
                                    <div style={{ fontSize: '0.75rem', color: 'var(--gray-600)' }}>
                                        {[person.email, person.phone].filter(Boolean).join(' • ')}
                                    </div>
                                </div>
                                <label className="toggle-switch">
                                    <input
                                        type="checkbox"
                                        checked={person.active}
                                        onChange={(e) => {
                                            setFormData(prev => ({
                                                ...prev,
                                                staff: prev.staff.map((s, i) => 
                                                    i === idx ? { ...s, active: e.target.checked } : s
                                                )
                                            }));
                                        }}
                                    />
                                    <span className="toggle-slider"></span>
                                </label>
                            </div>

                                <div style={{ marginBottom: '1rem' }}>
                                    <label style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-700)', display: 'block', marginBottom: '0.5rem' }}>
                                        Horarios de trabajo
                                    </label>
                                    <div style={{ fontSize: '0.75rem', color: 'var(--gray-600)', lineHeight: 1.4 }}>
                                        {/* Sin el respaldo, una persona guardada sin `schedule`
                                            tira la página entera a pantalla en blanco:
                                            Object.entries(undefined) es un TypeError y React
                                            desmonta todo el árbol. El horario del negocio es
                                            además el valor correcto, porque es el que hereda
                                            quien se crea desde el formulario. */}
                                        {Object.entries(person.schedule || formData.schedule || {}).map(([day, daySchedule]) => {
                                            if (!daySchedule?.enabled || !daySchedule.ranges?.length) return null;
                                            const dayName = dayNames[day];
                                            const ranges = daySchedule.ranges.map(r => `${r.start}-${r.end}`).join(', ');
                                            return (
                                                <div key={day}>
                                                    <strong>{dayName}:</strong> {ranges}
                                                </div>
                                            );
                                        }).filter(Boolean)}
                                        {Object.values(person.schedule || formData.schedule || {}).every(daySchedule => !daySchedule?.enabled) && (
                                            <span style={{ fontStyle: 'italic', opacity: 0.7 }}>Sin horarios definidos</span>
                                        )}
                                    </div>
                                </div>

                                        <div className="service-card-buttons">
                                        <button 
                                            className="service-card-button edit-btn"
                                            onClick={() => setEditingStaff(person)}
                                        >
                                            Editar Horarios
                                        </button>
                                        <button 
                                            className="service-card-button delete-btn"
                                            onClick={() => {
                                                if (window.confirm(`¿Eliminar a ${person.name} del personal?`)) {
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        staff: prev.staff.filter(s => s.id !== person.id)
                                                    }));
                                                }
                                            }}
                                        >
                                            Eliminar
                                        </button>
                                    </div>
                            </div>
                        ))}

                        {formData.staff.length === 0 && (
                            <div style={{ 
                                padding: '2rem', 
                                textAlign: 'center', 
                                color: 'var(--gray-500)', 
                                fontStyle: 'italic',
                                border: '1px dashed var(--gray-300)',
                                borderRadius: '8px'
                            }}>
                                Todavía no cargaste a nadie. Empezá con "+ Agregar Personal".
                    </div>
                        )}
                    </div>


                    </div>
                )}

                {/* Paso 5: Recursos */}
                {step === 5 && (
                    <div className={`onboarding-step ${isTransitioning ? 'transitioning' : ''}`}>
                        <h2>{getCurrentTemplate().resourcesTitle}</h2>

                        {/* La pregunta reemplaza a tres carteles de colores que había
                            antes —uno rojo de "obligatorio", uno verde de "perfecto" y
                            uno naranja con un tip— más un párrafo de bajada. Cuatro cajas
                            para decir algo que se resuelve con una pregunta de dos
                            botones, y ninguna explicaba lo único que importa: que esto es
                            cuántos turnos se pueden tomar a la misma hora. */}
                        <div className="recursos-pregunta">
                            <p className="recursos-pregunta-texto">
                                ¿{cuantos()} {getUnidad().plural} tenés?
                            </p>
                            <p className="recursos-pregunta-ayuda">
                                Es lo que define cuántos turnos podés tomar a la misma hora.
                            </p>

                            <div className="recursos-opciones">
                                <button
                                    type="button"
                                    className={`recursos-opcion ${modoRecursosEfectivo === 'uno' ? 'activa' : ''}`}
                                    onClick={elegirUnicoRecurso}
                                >
                                    <span className="recursos-opcion-titulo">{unoSolo()}</span>
                                    <span className="recursos-opcion-detalle">
                                        Un turno por vez, en el horario del negocio
                                    </span>
                                </button>

                                <button
                                    type="button"
                                    className={`recursos-opcion ${modoRecursosEfectivo === 'varios' ? 'activa' : ''}`}
                                    onClick={() => setModoRecursos('varios')}
                                >
                                    <span className="recursos-opcion-titulo">{masDeUno()}</span>
                                    <span className="recursos-opcion-detalle">
                                        Un turno por {getUnidad().singular}, en paralelo
                                    </span>
                                </button>
                            </div>
                        </div>

                        {/* Con una sola unidad no hay nada que administrar: se creó con el
                            horario del negocio y el paso termina acá. */}
                        {modoRecursosEfectivo === 'uno' && (
                            <p className="recursos-listo">
                                Listo. Vas a tomar un turno por vez, con el horario de atención que
                                cargaste. Si más adelante sumás {getUnidad().plural}, los agregás desde
                                el panel.
                            </p>
                        )}

                        {modoRecursosEfectivo === 'varios' && (
                            <>
                                <div style={{ marginBottom: '1.5rem' }}>
                                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                                        <button
                                            className="btn-primary btn-large-centered"
                                            onClick={() => setShowAddResourceModal(true)}
                                        >
                                            + Agregar {getUnidad().singular}
                                        </button>
                                    </div>

                                    {/* Los del rubro, con los horarios del negocio: un punto de
                                        partida para no arrancar de una pantalla vacía. */}
                                    {formData.resources.length === 0 && getRecursosSugeridos().length > 0 && (
                                        <div className="sugerencias-rubro">
                                            <button
                                                type="button"
                                                className="btn-secondary"
                                                onClick={handleCargarRecursosSugeridos}
                                            >
                                                Cargar los más habituales
                                            </button>
                                            <span className="sugerencias-rubro-nota">
                                                {getRecursosSugeridos().map(r => r.name).join(' · ')}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                    {formData.resources.map((resource, idx) => (
                                        <div key={resource.id || idx} className={`service-card ${!resource.active ? 'inactive' : ''}`}>
                                            <div className="service-header">
                                                <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                                    <h4 style={{ margin: '0 0 0.25rem 0', fontSize: '1rem', fontWeight: '600' }}>
                                                        {resource.name}
                                                    </h4>
                                                    <div style={{ fontSize: '0.75rem', color: 'var(--gray-600)' }}>
                                                        {resource.description}
                                                    </div>
                                                </div>
                                                <label className="toggle-switch">
                                                    <input
                                                        type="checkbox"
                                                        checked={resource.active !== false}
                                                        onChange={(e) => {
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                resources: prev.resources.map((r, i) =>
                                                                    i === idx ? { ...r, active: e.target.checked } : r
                                                                )
                                                            }));
                                                        }}
                                                    />
                                                    <span className="toggle-slider"></span>
                                                </label>
                                            </div>

                                            <div style={{ marginBottom: '1rem' }}>
                                                <label style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-700)', display: 'block', marginBottom: '0.5rem' }}>
                                                    Horarios de disponibilidad
                                                </label>
                                                <div style={{ fontSize: '0.75rem', color: 'var(--gray-600)', lineHeight: 1.4 }}>
                                                    {/* Sin el respaldo, un recurso guardado sin schedule
                                                        tira la página a pantalla en blanco: Object.entries
                                                        de undefined es un TypeError. */}
                                                    {Object.entries(resource.schedule || formData.schedule || {}).map(([day, daySchedule]) => {
                                                        if (!daySchedule?.enabled || !daySchedule.ranges?.length) return null;
                                                        const dayName = dayNames[day];
                                                        const ranges = daySchedule.ranges.map(r => `${r.start}-${r.end}`).join(', ');
                                                        return (
                                                            <div key={day}>
                                                                <strong>{dayName}:</strong> {ranges}
                                                            </div>
                                                        );
                                                    }).filter(Boolean)}
                                                    {Object.values(resource.schedule || formData.schedule || {}).every(daySchedule => !daySchedule?.enabled) && (
                                                        <span style={{ fontStyle: 'italic', opacity: 0.7 }}>Sin horarios definidos</span>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="service-card-buttons">
                                                <button
                                                    className="service-card-button edit-btn"
                                                    onClick={() => {
                                                        setEditingResourceIndex(idx);
                                                        setNewResourceData(resource);
                                                        setShowAddResourceModal(true);
                                                    }}
                                                >
                                                    Editar
                                                </button>
                                                <button
                                                    className="service-card-button delete-btn"
                                                    onClick={() => {
                                                        if (window.confirm(`¿Eliminar ${resource.name}?`)) {
                                                            handleRemoveResource(idx);
                                                        }
                                                    }}
                                                >
                                                    Eliminar
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* Paso 7: Servicios principales */}
                {step === 7 && (
                    <div className={`onboarding-step ${isTransitioning ? 'transitioning' : ''}`}>
                        <h2>{getCurrentTemplate().servicesTitle}</h2>
                        <p>{getCurrentTemplate().servicesDescription}</p>
                        
                        {/* Mensaje explicativo para servicios */}
                        {getServiceTipMessage().show && (
                            <div style={{ 
                                background: 'rgba(34, 197, 94, 0.1)', 
                                border: '1px solid rgba(34, 197, 94, 0.2)', 
                                borderRadius: '8px', 
                                padding: '1rem', 
                                marginBottom: '1.5rem',
                                textAlign: 'center'
                            }}>
                                <p style={{ 
                                    margin: 0, 
                                    fontSize: '0.875rem', 
                                    color: 'var(--gray-700)',
                                    fontWeight: '500'
                                }}>
                                    {/* El texto va como texto y el <strong> como JSX.
                                        Esto era un `dangerouslySetInnerHTML` con el mensaje
                                        entero: hoy los mensajes son constantes de este mismo
                                        archivo, así que no había forma de explotarlo — pero el
                                        día que uno venga del rubro guardado o de la API, se
                                        convierte en XSS sin que nada lo señale. El único HTML
                                        que aportaba era este <strong>. */}
                                    💡 <strong>Tip:</strong> {getServiceTipMessage().message}
                                </p>
                            </div>
                        )}
                        
                        {formData.services.length === 0 && (
                            <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
                                <button 
                                    type="button" 
                                    className="btn-secondary"
                                    onClick={() => setFormData(prev => ({
                                        ...prev,
                                        services: getCurrentTemplate().suggestedServices.map((service, index) => ({
                                            ...service,
                                            id: Date.now() + index,
                                            active: true,
                                            maxAdvanceBookingDays: 30,
                                            maxSimultaneousBookings: 1,
                                            allowWaitingList: false,
                                            allowCancellation: true,
                                            requiresDeposit: false,
                                            // Mismos campos que un servicio creado a mano
                                            // (handleAddService), para que no queden a medias
                                            staffSelectionMode: 'automatic',
                                            resourceSelectionMode: 'automatic',
                                            assignedStaff: [],
                                            assignedResources: [],
                                            priceVariations: [],
                                            enableDynamicPricing: false,
                                            depositConfig: {
                                                type: 'percentage',
                                                amount: 50,
                                                paymentMethods: formData.payments.methods.length > 0 ? [formData.payments.methods[0]] : ['efectivo'],
                                                cancellationPolicy: 'flexible'
                                            }
                                        }))
                                    }))}
                                    style={{ marginRight: '1rem' }}
                                >
                                    📋 Cargar servicios sugeridos
                                </button>
                                <span style={{ color: 'var(--gray-600)', fontSize: '0.875rem' }}>
                                    o agregá tus propios servicios
                                </span>
                            </div>
                        )}
                        
                        {/* Grid de servicios personalizado con 2 columnas */}
                        <div className="services-grid-onboarding">
                            {/* Agregar va PRIMERO, no al final.
                                Estaba último, después de todas las tarjetas, así que con
                                cuatro servicios cargados ya quedaba fuera de la pantalla
                                —medido: arrancaba a 1050px en una ventana de 900— y en
                                celular, donde la grilla es de una sola columna, a 2815px.
                                O sea que el botón para cargar los servicios propios sólo
                                aparecía scrolleando por encima de todos los sugeridos.
                                Primero se ve siempre, tenga el negocio dos servicios o
                                veinte. */}
                            <div className="service-card add-service-card" onClick={handleAddService}>
                                <div className="add-service-content">
                                    <div className="add-icon">+</div>
                                    <span>Agregar Servicio</span>
                                </div>
                            </div>

                            {/* Acá no hay servicios apagados: el que el negocio no hace
                                se BORRA, con el botón Eliminar de la tarjeta.

                                Había un interruptor para apagarlos, y dejaba la pantalla
                                llena de tarjetas grises de cosas que el negocio no ofrece
                                —los sugeridos del rubro que no le sirven— empujando abajo
                                de todo la tarjeta de "Agregar Servicio", que es lo que hace
                                falta para cargar los propios. Apagar es una idea del panel,
                                donde tiene sentido guardar un servicio de temporada; en el
                                alta, "no lo hago" quiere decir que no va. */}
                            {formData.services.map((service, index) => (
                                <div key={service.id || index} className="service-card">
                                    <div className="service-header">
                                        <h3>{service.name}</h3>
                                    </div>

                                    <div className="service-details">
                                        <div className="service-detail-item">
                                            <label>Precio:</label>
                                            <div className="price-display">
                                                <span>$</span>
                                                <span>{service.price}</span>
                                            </div>
                                        </div>
                                        
                                        <div className="service-detail-item">
                                            <label>Duración:</label>
                                            <div className="duration-display">
                                                <span>{service.duration}</span>
                                                <span>min</span>
                                            </div>
                                        </div>
                                        
                                        <div className="service-detail-item">
                                            <label>Personal:</label>
                                            <div className="staff-display">
                                                <span>{service.assignedStaff?.length > 0 ? `${service.assignedStaff.length} asignado(s)` : 'Automático'}</span>
                                            </div>
                                        </div>
                                        
                                        <div className="service-detail-item">
                                            <label>Recursos:</label>
                                            <div className="resources-display">
                                                <span>{service.assignedResources?.length > 0 ? `${service.assignedResources.length} asignado(s)` : 'Automático'}</span>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="service-card-buttons">
                                        <button
                                            className="service-card-button edit-btn"
                                            onClick={() => handleEditService(index)}
                                            title="Editar servicio"
                                        >
                                            <span>Editar</span>
                                        </button>
                                        <button
                                            className="service-card-button delete-btn"
                                            onClick={() => handleDeleteService(index)}
                                            title="Eliminar servicio"
                                        >
                                            <span>Eliminar</span>
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>

                    </div>
                )}

                {/* Paso 8: Branding */}
                {step === 8 && (
                    <div className={`onboarding-step ${isTransitioning ? 'transitioning' : ''}`}>
                        <h2>🎨 Imagen de marca</h2>
                        <p>Personalizá los colores y la apariencia de tu página de reservas para que refleje la identidad de tu negocio.</p>
                        
                        {/* Los colores se eligen acá mismo, mirando la página real
                            que está justo debajo. Antes había un botón "Cambiar
                            colores" que abría un modal con SU PROPIA vista previa,
                            una maqueta escrita aparte que ya no se parecía a la
                            página: decía "Paso 1: Elige tu Servicio" cuando la
                            página dice "Elegí el servicio", y mostraba un botón
                            "Gestionar Turno" que no existe más. */}
                        <SelectorDeColores
                            value={formData.branding}
                            onChange={(branding) => setFormData(prev => ({ ...prev, branding }))}
                        />

                        {/* La imagen del negocio, el mismo componente que el panel
                            (components/business/SelectorDeImagen.jsx).

                            Acá no había forma de poner una foto: el paso ofrecía
                            elegir el color y nada más, así que la página se
                            estrenaba con las iniciales del negocio y para cambiar
                            eso había que terminar el onboarding, entrar al panel y
                            buscar Personalización de Marca.

                            Sube a Firebase Storage y guarda la URL, igual que el
                            panel. Había acá un `handleLogoChange` que leía el
                            archivo como data URL (base64) y lo metía en el estado:
                            no lo usaba ningún input —quedó suelto cuando se sacó
                            el modal viejo— y además no es el camino, porque una
                            foto en base64 se guarda entera adentro del documento
                            del negocio y viaja en cada lectura. */}
                        <SelectorDeImagen
                            value={formData.branding?.logo || null}
                            onChange={(logo) => setFormData(prev => ({
                                ...prev,
                                branding: { ...prev.branding, logo }
                            }))}
                            uid={currentUser?.uid}
                            nombreDelNegocio={formData.businessInfo?.name}
                        />

                        {/* La página de verdad, con los datos que se vienen cargando.
                            Antes había que apretar "Vista Previa Interactiva" para
                            ver una maqueta que además no era la página real: ahora
                            se ve sola al llegar a este paso y es el mismo
                            componente que sirve /<slug>, así que lo que se ve acá
                            es exactamente lo que van a ver los clientes. */}
                        <VistaPreviaNegocio datos={datosParaPreview} />
                    </div>
                )}

                {/* Paso 6: Eliminado - configuración de pagos se hace después */}
                
                {/* Paso 9: Integraciones */}
                {step === 9 && (
                    <div className={`onboarding-step ${isTransitioning ? 'transitioning' : ''}`}>
                        <div className="section-header">
                            <h2>Centro de Integraciones</h2>
                            <p>Conecta Aturno con tus herramientas favoritas y automatiza tu negocio</p>
                        </div>

                        {/* Grid principal de integraciones */}
                        <div className="integrations-main-grid">
                                                         {availableIntegrations.map(integration => {
                                const integrationData = integrations[integration.key];
                                const isConnected = integrationData?.connected || false;
                                const isConnecting = connectingIntegration === integration.key;
                                
                                return (
                                    <div key={integration.key} className={`integration-card ${isConnected ? 'connected' : 'disconnected'}`}>
                                        {/* Header de la card */}
                                        <div className="integration-card-header">
                                            <div className="integration-info">
                                                {/* El recuadro toma el color de la marca de la
                                                    integración, no un blanco neutro: es lo que hace
                                                    que Google se lea como Google y Mercado Pago como
                                                    Mercado Pago de un vistazo. El `1a` del final es
                                                    la transparencia en hexadecimal, un 10%. */}
                                                <div
                                                    className="integration-icon-wrapper"
                                                    style={{ backgroundColor: `${integration.color}1a`, color: integration.color }}
                                                >
                                                    {integration.icon}
                                                </div>
                                                <div className="integration-details">
                                                    <h4>{integration.name}</h4>
                                                    <span className="integration-category">{integration.category}</span>
                            </div>
                        </div>
                                            <div className={`status-indicator ${isConnected ? 'connected' : 'disconnected'}`}>
                                                {integration.proximamente ? (
                                                    <span className="status-badge proximamente">
                                                        Próximamente
                                                    </span>
                                                ) : isConnected ? (
                                                    <span className="status-badge connected">
                                                        <span className="status-dot"></span>
                                                        Conectado
                                                    </span>
                                                ) : (
                                                    <span className="status-badge disconnected">
                                                        <span className="status-dot"></span>
                                                        Desconectado
                                                    </span>
                                                )}
                    </div>
                                        </div>

                                        {/* Descripción */}
                                        <p className="integration-description">
                                            {integration.description}
                                        </p>

                                        {/* Beneficios */}
                                        <div className="integration-benefits">
                                            <h5>Beneficios:</h5>
                                            <ul>
                                                {integration.benefits.map((benefit, index) => (
                                                    <li key={index}>
                                                        <span className="benefit-check">✓</span>
                                                        {benefit}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>

                                        {/* Estado de conexión y acciones */}
                                        <div className="integration-actions">
                                            {integration.proximamente ? (
                                                <div className="connection-actions">
                                                    <button className="btn-integration-primary" disabled>
                                                        Todavía no disponible
                                                    </button>
                                                    <small className="connection-note">
                                                        Te avisamos cuando esté lista
                                                    </small>
                                                </div>
                                            ) : isConnected ? (
                                                <>
                                                    <div className="connection-info">
                                                        <span className="connection-detail">
                                                            Conectado recientemente
                                                        </span>
                                                    </div>
                                                    <div className="action-buttons">
                                                        <button 
                                                            className="btn-integration-secondary"
                                                            onClick={() => alert(`Configurando ${integration.name}...`)}
                                                        >
                                                            Configurar
                                                        </button>
                                                        <button 
                                                            className="btn-integration-danger"
                                                            onClick={() => handleDisconnect(integration.key)}
                                                        >
                                                            Desconectar
                                                        </button>
                                                    </div>
                                                </>
                                            ) : (
                                                <div className="connection-actions">
                                                    {isConnecting ? (
                                                        <button className="btn-integration-primary connecting" disabled>
                                                            Conectando…
                                                        </button>
                                                    ) : (
                                                        <button 
                                                            className="btn-integration-primary"
                                                            onClick={() => handleConnect(integration.key)}
                                                            style={{ backgroundColor: integration.color }}
                                                        >
                                                            Conectar
                                                        </button>
                                                    )}
                                                    <small className="connection-note">
                                                        Gratis en tu plan actual
                                                    </small>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Sección de beneficios de integraciones */}

                        {/* Próximamente */}
                    </div>
                )}

                {/* Paso 10: Selección de plan */}
                {step === 10 && (
                    <div className={`onboarding-step ${isTransitioning ? 'transitioning' : ''}`}>
                        <h2>Elegí tu plan</h2>
                        <p>Elegí el que mejor le sirva a tu negocio. Lo podés cambiar o cancelar cuando quieras.</p>
                        
                        {/* Acá había un banner verde grande con "Probar 14 días GRATIS"
                            y, debajo, un separador que decía "o elegí un plan para pagar
                            ahora". Se eliminó: la tarjeta de Business ya trae su propio
                            botón de prueba, así que el mismo trámite se ofrecía dos veces
                            en la misma pantalla, y el de arriba además elegía el plan por
                            vos sin que se viera cuál. */}

                        <PricingSection 
                            showCurrentPlan={false}
                            isOnboarding={true}
                            onPlanSelect={(planId, isTrial) => {
                                setFormData(prev => ({ 
                                    ...prev, 
                                    selectedPlan: planId,
                                    selectedBillingCycle: billingCycle,
                                    isTrialPlan: isTrial,
                                    freeTrialEndDate: isTrial ? new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString() : null
                                }));
                                
                                // Si es trial, avanzar automáticamente
                                if (isTrial) {
                                    setTimeout(() => {
                                        goToStep(step + 1);
                                    }, 800);
                                } else {
                                    // Si el usuario elige pagar ahora, podríamos redirigir directamente a MercadoPago
                                    console.log('Usuario eligió pagar ahora el plan:', planId);
                                    // TODO: Implementar redirección a pago
                                }
                            }}
                        />
                        
                        {formData.selectedPlan && (
                            <div className="plan-selection-note">
                                {formData.isTrialPlan ? (
                                    <p>✅ <strong>Genial!</strong> Tenés 14 días gratis para probar el plan <strong>{nombreDePlan(formData.selectedPlan)}</strong>. Lo podés cambiar o cancelar cuando quieras.</p>
                                ) : (
                                    <p>💳 <strong>Excelente elección!</strong> Al terminar te llevamos a completar el pago del plan <strong>{nombreDePlan(formData.selectedPlan)}</strong>.</p>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Paso 11: Revisión final */}
                {step === 11 && (
                    <div className={`onboarding-step ${isTransitioning ? 'transitioning' : ''}`}>
                        {/* El botón de crear va ARRIBA, antes de la vista previa.
                            Estaba al final, después de una página entera de
                            contenido: para llegar había que recorrerla toda, y lo
                            que se está confirmando es justamente lo que se ve, no
                            algo que haya que leer hasta el fondo.

                            También se sacó el título "¡Perfecto! Tu negocio está
                            listo": todavía no está creado —crearlo es apretar este
                            botón— y encima repetía lo que dice el botón. */}
                        <div className="final-action-container">
                            <div className="action-message">
                                <h2>¿Todo se ve bien?</h2>
                                {formData.isTrialPlan ? (
                                    <p>Así la van a ver tus clientes. Todo esto lo podés editar después desde el panel, y tu prueba de 14 días empieza al crear el negocio.</p>
                                ) : (
                                    <p>Así la van a ver tus clientes. Al finalizar te llevamos a Mercado Pago para completar el pago de tu plan <strong>{nombreDePlan(formData.selectedPlan)}</strong>; cuando se confirme, creamos tu negocio.</p>
                                )}
                            </div>
                            {/* La aceptación va acá, pegada al botón que crea el
                                negocio y contrata el plan. En el registro se
                                acepta para tener un usuario; esto es otra cosa:
                                a partir de este botón hay un cobro mensual. */}
                            <label className={`aceptacion-legal ${aceptaLegales ? 'aceptada' : ''}`}>
                                <input
                                    type="checkbox"
                                    checked={aceptaLegales}
                                    onChange={(e) => setAceptaLegales(e.target.checked)}
                                    disabled={loading}
                                />
                                <span>
                                    Acepto los{' '}
                                    <button
                                        type="button"
                                        className="enlace-legal"
                                        onClick={() => setShowTermsModal(true)}
                                    >
                                        Términos y Condiciones
                                    </button>{' '}
                                    y la{' '}
                                    <button
                                        type="button"
                                        className="enlace-legal"
                                        onClick={() => setShowPrivacyModal(true)}
                                    >
                                        Política de Privacidad
                                    </button>
                                    .
                                </span>
                            </label>

                            <button
                                className="btn-create-business"
                                onClick={handleFinish}
                                disabled={loading || !aceptaLegales}
                            >
                                {loading ? (
                                    <>
                                        <span className="loading-spinner"></span>
                                        {formData.isTrialPlan ? 'Creando tu negocio...' : 'Procesando pago...'}
                                    </>
                                ) : (
                                    formData.isTrialPlan ? 'Crear mi negocio' : 'Finalizar y pagar'
                                )}
                            </button>
                        </div>

                        {/* Antes acá había un resumen en tarjetas de todo lo elegido.
                            Lo reemplaza la página real: lo que importa antes de crear
                            el negocio no es releer los datos que uno mismo acaba de
                            cargar, sino ver cómo le va a quedar a sus clientes.

                            `completa`: sin marco con scroll propio. En este paso la
                            página se muestra entera y lo que scrollea es la tarjeta
                            del paso, una sola vez. Con las dos zonas de scroll una
                            adentro de la otra no se entendía cuál se estaba moviendo. */}
                        <VistaPreviaNegocio datos={datosParaPreview} completa />
                    </div>
                )}

                {/* Modal para agregar nuevo personal */}
                {showNewStaffForm && (
                    <div className="modal-overlay" onClick={() => setShowNewStaffForm(false)}>
                        {/* Ancho fijo, el mismo con el calendario abierto y cerrado.
                            Estaba condicionado a "Uno propio" y el modal saltaba de 500
                            a 1200px al elegirlo: el formulario se corría entero debajo
                            del dedo justo después de tocar. */}
                        <div className="modal-content staff-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h3>Agregar personal</h3>
                                <button className="modal-close" onClick={() => setShowNewStaffForm(false)}>×</button>
                            </div>

                            {/* Solo el nombre es obligatorio. Antes pedía nombre, email
                                y teléfono, los tres, y el paso entero no deja continuar
                                sin al menos una persona cargada: un negocio que no tiene
                                el mail de cada empleado quedaba trabado en el onboarding.

                                El formulario es el mismo componente que usa la edición,
                                y va partido en cuatro partes plegables como el detalle
                                de servicio: de los seis campos solo el nombre hace falta,
                                y una lista plana no deja ver cuáles de los opcionales ya
                                miraste. */}
                            <div className="staff-form-container">
                                <FormularioDePersona
                                    valor={nuevoStaff}
                                    onChange={setNuevoStaff}
                                    uid={currentUser?.uid}
                                    catalogoDeRoles={catalogoDeRoles(formData.staff, getRolesSugeridos())}
                                    rolesSugeridos={getRolesSugeridos()}
                                    onBorrarRol={borrarRolDelEquipo}
                                    usosDelRol={(rol) => cuantosUsanElRol(formData.staff, rol)}
                                    scheduleDelNegocio={formData.schedule}
                                    dayNames={dayNames}
                                    conEleccionDeHorario
                                    sufijoDeId="alta"
                                    textoGuardar="Agregar"
                                    error={errorStaff}
                                    onGuardar={() => {
                                        const name = (nuevoStaff.name || '').trim();
                                        if (!name) {
                                            setErrorStaff('Poné el nombre de la persona.');
                                            document.getElementById('persona-nombre-alta')?.focus();
                                            return;
                                        }

                                        setFormData(prev => ({
                                            ...prev,
                                            staff: [
                                                ...prev.staff,
                                                {
                                                    id: Date.now(),
                                                    name,
                                                    roles: nuevoStaff.roles || [],
                                                    // Opcional: sin foto se muestran las iniciales.
                                                    foto: nuevoStaff.foto || null,
                                                    // El campo viejo se sigue escribiendo: ver rolParaGuardar.
                                                    rol: rolParaGuardar(nuevoStaff.roles),
                                                    email: (nuevoStaff.email || '').trim(),
                                                    phone: (nuevoStaff.phone || '').trim(),
                                                    active: true,
                                                    // Copia propia del horario: si fuera una referencia al
                                                    // del negocio, editar el de una persona cambiaría el de
                                                    // todas.
                                                    schedule: JSON.parse(JSON.stringify(
                                                        nuevoStaff.horarioPropio && nuevoStaff.schedule
                                                            ? nuevoStaff.schedule
                                                            : prev.schedule
                                                    )),
                                                },
                                            ],
                                        }));

                                        setErrorStaff('');
                                        setShowNewStaffForm(false);
                                    }}
                                />
                            </div>

                            {/* Solo Cancelar: el de guardar es ahora el pie de la última
                                parte, como en el detalle de servicio. Tenerlo en los dos
                                lados invita a apretar el de abajo sin haber pasado por
                                las partes, que es lo que el recorrido trata de evitar. */}
                            <div className="modal-actions">
                                <button
                                    type="button"
                                    className="btn-secondary"
                                    onClick={() => setShowNewStaffForm(false)}
                                >
                                    Cancelar
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal para editar personal */}
                {/* Modal para editar personal.

                    Antes tenía su propio editor de horarios —siete columnas con
                    desplegables de hora y minuto por día—, que es el que se
                    reemplazó en el paso de horarios del negocio; acá había
                    quedado la copia vieja. Ahora usa el mismo calendario, así el
                    horario de una persona se carga igual que el del negocio.

                    Y pedía nombre, email y teléfono para poder guardar, mientras
                    que el alta solo pide el nombre: una persona cargada sin mail
                    no se podía volver a editar nunca, porque el botón de guardar
                    quedaba deshabilitado para siempre. */}
                {editingStaff && (
                    <div className="modal-overlay" onClick={() => setEditingStaff(null)}>
                        <div className="modal-content staff-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h3>Editar personal</h3>
                                <button className="modal-close" onClick={() => setEditingStaff(null)}>×</button>
                            </div>

                            <div className="staff-form-container">
                                <FormularioDePersona
                                    valor={editingStaff}
                                    onChange={setEditingStaff}
                                    uid={currentUser?.uid}
                                    catalogoDeRoles={catalogoDeRoles(formData.staff, getRolesSugeridos())}
                                    rolesSugeridos={getRolesSugeridos()}
                                    onBorrarRol={borrarRolDelEquipo}
                                    usosDelRol={(rol) => cuantosUsanElRol(formData.staff, rol)}
                                    scheduleDelNegocio={formData.schedule}
                                    dayNames={dayNames}
                                    sufijoDeId="edicion"
                                    textoGuardar="Guardar cambios"
                                    onGuardar={() => {
                                        setFormData(prev => ({
                                            ...prev,
                                            staff: prev.staff.map(s =>
                                                s.id === editingStaff.id
                                                    // `rol` se recalcula al guardar y no se arrastra el que
                                                    // tenía: si no, alguien que edita sus roles queda con la
                                                    // lista nueva y el texto viejo, y cada uno dice una cosa.
                                                    ? { ...editingStaff, rol: rolParaGuardar(rolesDe(editingStaff)) }
                                                    : s
                                            )
                                        }));
                                        setEditingStaff(null);
                                    }}
                                />
                            </div>

                            <div className="modal-actions">
                                <button className="btn-secondary" onClick={() => setEditingStaff(null)}>
                                    Cancelar
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal para agregar/editar recursos */}
                {/* Alta y edición de un espacio reservable, con la misma forma
                    que el alta de personal: nombre obligatorio, descripción
                    opcional y el calendario para el horario. Tenía la estructura
                    vieja —un bloque "Información del Recurso" y siete columnas de
                    desplegables de hora y minuto—, que es la que se reemplazó en
                    el resto de la app. */}
                {showAddResourceModal && (
                    <div className="modal-overlay" onClick={() => setShowAddResourceModal(false)}>
                        <div className="modal-content staff-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h3>{editingResourceIndex !== null ? 'Editar' : `Agregar ${getUnidad().singular}`}</h3>
                                <button className="modal-close" onClick={() => setShowAddResourceModal(false)}>×</button>
                            </div>

                            <div className="staff-form-container">
                                <div className="form-group">
                                    <label htmlFor="recurso-nombre">
                                        Nombre <span className="campo-obligatorio">*</span>
                                    </label>
                                    <input
                                        id="recurso-nombre"
                                        type="text"
                                        value={newResourceData.name}
                                        onChange={(e) => setNewResourceData(prev => ({ ...prev, name: e.target.value }))}
                                        placeholder={getPlaceholderRecurso()}
                                        autoFocus
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="recurso-descripcion">Descripción (opcional)</label>
                                    <input
                                        id="recurso-descripcion"
                                        type="text"
                                        value={newResourceData.description}
                                        onChange={(e) => setNewResourceData(prev => ({ ...prev, description: e.target.value }))}
                                        placeholder="Para qué se usa"
                                    />
                                </div>

                                <div className="staff-horario">
                                    <label className="staff-horario-titulo">Horario</label>
                                    <BusinessScheduleForm
                                        value={newResourceData.schedule || {}}
                                        onChange={(schedule) => setNewResourceData(prev => ({ ...prev, schedule }))}
                                        dayNames={dayNames}
                                    />
                                </div>
                            </div>

                            <div className="modal-actions">
                                <button
                                    type="button"
                                    className="btn-secondary"
                                    onClick={() => {
                                        setShowAddResourceModal(false);
                                        setEditingResourceIndex(null);
                                    }}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="button"
                                    className="btn-primary"
                                    onClick={handleAddResourceSubmit}
                                    disabled={!newResourceData.name.trim()}
                                >
                                    {editingResourceIndex !== null ? 'Guardar cambios' : 'Agregar'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Acá se montaba BrandPreview, el modal de "Cambiar colores".
                    Salió del onboarding: los colores se eligen en el propio paso,
                    con la página real a la vista. El componente sigue existiendo
                    porque lo usa el panel, donde arrastra la misma maqueta vieja;
                    unificarlo entra en la paridad panel ↔ onboarding. */}

                {/* Terms of Service Modal */}
                {showTermsModal && (
                    <div className="modal-overlay" onClick={() => setShowTermsModal(false)}>
                        <div className="modal-content terms-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h3>Términos y Condiciones</h3>
                                <button className="modal-close" onClick={() => setShowTermsModal(false)}>×</button>
                            </div>
                            <div className="modal-body">
                                <TermsOfService />
                            </div>
                        </div>
                    </div>
                )}

                {/* Privacy Policy Modal */}
                {showPrivacyModal && (
                    <div className="modal-overlay" onClick={() => setShowPrivacyModal(false)}>
                        <div className="modal-content privacy-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h3>Política de Privacidad</h3>
                                <button className="modal-close" onClick={() => setShowPrivacyModal(false)}>×</button>
                            </div>
                            <div className="modal-body">
                                <PrivacyPolicy />
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal para agregar/editar servicios */}
                {showServiceModal && serviceModalData && (
                    <div className="modal-overlay" onClick={handleCancelServiceModal}>
                        <div className="modal-content service-modal" onClick={(e) => e.stopPropagation()}>
                            <EditableServiceCard
                                service={serviceModalData}
                                onSave={handleSaveService}
                                onCancel={handleCancelServiceModal}
                                onDelete={editingServiceIndex !== null ? () => handleDeleteService(editingServiceIndex) : undefined}
                                businessHours={formData.schedule}
                                availableStaff={formData.staff}
                                availableResources={formData.resources}
                                availablePaymentMethods={formData.payments.methods}
                                isEditing={true}
                                showDeleteButton={editingServiceIndex !== null}
                                userType={formData.userType}
                                serviciosExistentes={formData.services}
                            />
                        </div>
                    </div>
                )}


            </div>

            {/* Navegación: un punto por paso, con flechas a los costados. En el
                último paso no va "Siguiente" porque el propio paso tiene el
                botón de crear el negocio.

                Va fuera de .onboarding-content —no adentro, como estaba— para
                que quede fijo al pie. Adentro era el último elemento del
                contenido, así que se hundía tanto como largo fuera el paso: en
                55 de 60 mediciones (6 tamaños de pantalla × 10 pasos) las
                flechas nacían fuera de la pantalla y había que scrollear a
                ciegas para encontrar cómo avanzar. Ahora el contenedor reparte
                la altura en tres: cabecera, contenido que scrollea, y esta
                barra siempre visible. */}
            <div className="navigation-buttons-container">
                <PasoNavegador
                    pasos={pasosVisibles}
                    pasoActual={step}
                    onAnterior={() => goToStep(step - 1)}
                    onSiguiente={() => goToStep(step + 1)}
                    onIrAPaso={(paso) => goToStep(paso)}
                    puedeAvanzar={canGoToNextStep()}
                    mostrarSiguiente={step !== 11}
                    deshabilitado={loading || isTransitioning}
                    motivoBloqueo={
                        step === 4
                            ? 'Agregá al menos un miembro del personal para continuar'
                            : step === 5
                            ? 'Agregá al menos un recurso para continuar'
                            : 'Completá los campos obligatorios para continuar'
                    }
                />
            </div>
        </div>
            )}
        </>
    );
} 